import React, { useState, useEffect, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  RefreshCw,
  Truck,
  Target,
  Package,
  AlertTriangle,
  Building2,
  FileText,
  ChevronUp,
  ChevronDown,
  CheckCircle,
  ExternalLink,
  Warehouse,
  DollarSign,
  ArrowDownToLine,
  ArrowUpFromLine,
  Archive,
  Download // CHA-466
} from 'lucide-react';
// CHA-466 — pure CSV builders (tested separately)
import { buildDimsCsv, buildMissingCsv, timestampedName } from '../utils/dimsCsv';

export default function LogisticsPage({ onBackToDashboard, userProfile, tenantId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Section state - 'warehouse' or 'tms' (Customer TMS)
  const [activeSection, setActiveSection] = useState('warehouse');
  // Tab state - warehouse: 'bl', 'lap', 'capacity', 'billing' | tms: 'walmart', 'target'
  const [activeTab, setActiveTab] = useState('bl');
  // Billing sub-state
  const [billingOwner, setBillingOwner] = useState('BL');
  const [billingTab, setBillingTab] = useState('storage');
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [excludeUnder30, setExcludeUnder30] = useState(false);
  const [inboundData, setInboundData] = useState(null);
  const [inboundLoading, setInboundLoading] = useState(false);
  // Default to last week Mon-Sun
  const getLastMonday = () => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return d.toISOString().slice(0, 10);
  };
  const getLastSunday = () => {
    const d = new Date(getLastMonday()); d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  };
  const [inboundFrom, setInboundFrom] = useState(getLastMonday);
  const [inboundTo, setInboundTo] = useState(getLastSunday);
  const [outboundData, setOutboundData] = useState(null);
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [outboundFrom, setOutboundFrom] = useState(getLastMonday);
  const [outboundTo, setOutboundTo] = useState(getLastSunday);

  // Walmart MABD Matrix data
  const [mabdMatrix, setMabdMatrix] = useState(null);

  // Notion-style view switcher for Walmart groups
  const [activeGroupView, setActiveGroupView] = useState('InMocean');

  // Selected cell in Table 1 for filtering Table 3
  // { mabd: '12/15', appointmentDate: '12/16/2025' } or { mabd: '12/15', appointmentDate: 'Pending' }
  const [selectedCell, setSelectedCell] = useState(null);

  // Selected cell in Table 2 (Pending Matrix) for filtering Table 3
  // { mabd: '12/15', pickupWindow: '12/10/2025' }
  const [selectedPendingCell, setSelectedPendingCell] = useState(null);

  // Sort direction for MABD in each table: 'asc' (closest first) or 'desc' (later first)
  const [table1SortDir, setTable1SortDir] = useState('asc');
  const [table2SortDir, setTable2SortDir] = useState('asc');
  const [table4SortDir, setTable4SortDir] = useState('asc');

  // Department filter for Table 3
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Selected PO for Table 6 (style details)
  const [selectedPO, setSelectedPO] = useState(null);

  // Selected MABD from Table 4 (Open Orders)
  const [selectedOpenMABD, setSelectedOpenMABD] = useState(null);

  // BL/LAP Log state
  const [blLogData, setBlLogData] = useState(null);
  const [blLogLoading, setBlLogLoading] = useState(false);
  const [blLogSyncing, setBlLogSyncing] = useState(false);
  const [blLogLastSync, setBlLogLastSync] = useState(null);
  const [blLogSort, setBlLogSort] = useState({ key: 'recv_date', dir: 'desc' });
  const [blLogFilters, setBlLogFilters] = useState({ log_division: '', rabi_division: '', ship_to: '', shipped: '', routing_status: '' });
  const [lapLogData, setLapLogData] = useState(null);
  const [lapLogLoading, setLapLogLoading] = useState(false);
  const [lapLogSyncing, setLapLogSyncing] = useState(false);
  const [lapLogLastSync, setLapLogLastSync] = useState(null);
  const [lapLogSort, setLapLogSort] = useState({ key: 'rcvd_date', dir: 'desc' });
  const [lapLogFilters, setLapLogFilters] = useState({ log_division: '', rabi_division: '', ship_to: '', shipped: '', customer_name: '', pick_ticket: '' });

  // Container Log state
  const [containerLogData, setContainerLogData] = useState(null);
  const [containerLogLoading, setContainerLogLoading] = useState(false);
  const [containerLogLastSync, setContainerLogLastSync] = useState(null);
  const [containerLogSort, setContainerLogSort] = useState({ key: 'wh_arrival_date', dir: 'desc' });
  const [containerLogFilters, setContainerLogFilters] = useState({ wh_code: '', status: '', source: '', container_number: '', days_range: '' });

  // Routing Notes state
  const [routingNotes, setRoutingNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSearching, setNotesSearching] = useState(false);
  const [notesSearch, setNotesSearch] = useState({ type: 'po', value: '' });
  const [searchedOrder, setSearchedOrder] = useState(null); // Order data from BigQuery
  const [editingNote, setEditingNote] = useState(null); // null = new note, or note object for editing
  const [noteForm, setNoteForm] = useState({
    lookup_type: 'po',
    lookup_value: '',
    original_appt_date: '',
    override_appt_date: '',
    mabd: '',
    reason: '',
    responsibility: '',
    note: ''
  });

  // Routing overrides for Walmart display (from routing notes with override dates)
  const [routingOverrides, setRoutingOverrides] = useState({ byPO: {}, byPickTicket: {}, byLoad: {} });

  // Target Routing state
  const [targetData, setTargetData] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetSelectedCell, setTargetSelectedCell] = useState(null);
  const [targetSelectedPO, setTargetSelectedPO] = useState(null);
  const [targetSortDir, setTargetSortDir] = useState('asc');

  // CHA-463 — Target sub-tabs: 'routing' (the existing matrix) | 'dims' (item dims coverage)
  const [targetSubTab, setTargetSubTab] = useState('routing');
  const [dimsData, setDimsData] = useState(null);
  const [dimsLoading, setDimsLoading] = useState(false);
  const [dimsRebuilding, setDimsRebuilding] = useState(false);
  const [dimsRebuildResult, setDimsRebuildResult] = useState(null);
  const [dimsView, setDimsView] = useState('missing'); // 'missing' | 'ready'
  const [dimsUnstableAll, setDimsUnstableAll] = useState(false); // CHA-467 — show every unstable item, not just the first 10
  const [manualDimDraft, setManualDimDraft] = useState(null);
  const [manualDimSaving, setManualDimSaving] = useState(false);
  const targetDimsCanEditManual = ['admin', 'platform_owner', 'platform_admin'].includes(userProfile?.role);

  // Google Sheet configuration for sync
  const ROUTING_SHEET_ID = '1PjCjraQLyCu6AtgOyRW7tu_LgTJ227Bz4zJqiMuHnf8';
  const ROUTING_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1PjCjraQLyCu6AtgOyRW7tu_LgTJ227Bz4zJqiMuHnf8/edit';

  // Memoized filtered/sorted BL Log data
  const filteredBlLogData = useMemo(() => {
    if (!blLogData) return [];
    let data = [...blLogData];

    // Apply filters
    if (blLogFilters.log_division) {
      data = data.filter(r => r.log_division === blLogFilters.log_division);
    }
    if (blLogFilters.rabi_division) {
      data = data.filter(r => r.rabi_division === blLogFilters.rabi_division);
    }
    if (blLogFilters.ship_to) {
      data = data.filter(r => r.ship_to === blLogFilters.ship_to);
    }
    if (blLogFilters.shipped === 'Y') {
      data = data.filter(r => r.shipped === 'Y' || r.shipped === 'Yes');
    } else if (blLogFilters.shipped === 'N') {
      data = data.filter(r => !r.shipped || (r.shipped !== 'Y' && r.shipped !== 'Yes'));
    }
    if (blLogFilters.routing_status) {
      data = data.filter(r => r.routing_status === blLogFilters.routing_status);
    }

    // Apply sort
    if (blLogSort.key) {
      data.sort((a, b) => {
        const aVal = a[blLogSort.key] || '';
        const bVal = b[blLogSort.key] || '';
        const cmp = aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        return blLogSort.dir === 'asc' ? cmp : -cmp;
      });
    }

    return data;
  }, [blLogData, blLogFilters, blLogSort]);

  // Memoized filtered/sorted LAP Log data
  const filteredLapLogData = useMemo(() => {
    if (!lapLogData) return [];
    let data = [...lapLogData];

    // Apply filters
    if (lapLogFilters.log_division) {
      data = data.filter(r => r.log_division === lapLogFilters.log_division);
    }
    if (lapLogFilters.rabi_division) {
      data = data.filter(r => r.rabi_division === lapLogFilters.rabi_division);
    }
    if (lapLogFilters.ship_to) {
      data = data.filter(r => r.ship_to === lapLogFilters.ship_to);
    }
    if (lapLogFilters.shipped === 'Y') {
      data = data.filter(r => r.shipped === 'Y' || r.shipped === 'Yes');
    } else if (lapLogFilters.shipped === 'N') {
      data = data.filter(r => !r.shipped || (r.shipped !== 'Y' && r.shipped !== 'Yes'));
    }
    if (lapLogFilters.customer_name) {
      data = data.filter(r => r.customer_name === lapLogFilters.customer_name);
    }
    if (lapLogFilters.pick_ticket) {
      const search = lapLogFilters.pick_ticket.toLowerCase();
      data = data.filter(r => r.pick_ticket && r.pick_ticket.toLowerCase().includes(search));
    }

    // Apply sort
    if (lapLogSort.key) {
      data.sort((a, b) => {
        const aVal = a[lapLogSort.key] || '';
        const bVal = b[lapLogSort.key] || '';
        const cmp = aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        return lapLogSort.dir === 'asc' ? cmp : -cmp;
      });
    }

    return data;
  }, [lapLogData, lapLogFilters, lapLogSort]);

  // Helper to get days range bucket
  const getDaysRange = (days) => {
    const d = parseInt(days) || 0;
    if (d <= 3) return '0-3';
    if (d <= 7) return '4-7';
    if (d <= 14) return '8-14';
    if (d <= 21) return '15-21';
    return '22+';
  };

  // Memoized filtered/sorted Container Log data
  const filteredContainerLogData = useMemo(() => {
    if (!containerLogData) return [];
    let data = [...containerLogData];

    // Apply filters
    if (containerLogFilters.wh_code) {
      data = data.filter(r => r.wh_code === containerLogFilters.wh_code);
    }
    if (containerLogFilters.status) {
      data = data.filter(r => r.status === containerLogFilters.status);
    }
    if (containerLogFilters.source) {
      data = data.filter(r => r.source === containerLogFilters.source);
    }
    if (containerLogFilters.container_number) {
      const search = containerLogFilters.container_number.toLowerCase();
      data = data.filter(r => r.container_number && r.container_number.toLowerCase().includes(search));
    }
    if (containerLogFilters.days_range) {
      data = data.filter(r => getDaysRange(r.days_on_site) === containerLogFilters.days_range);
    }

    // Apply sort
    if (containerLogSort.key) {
      data.sort((a, b) => {
        const aVal = a[containerLogSort.key] || '';
        const bVal = b[containerLogSort.key] || '';
        const cmp = aVal.toString().localeCompare(bVal.toString(), undefined, { numeric: true });
        return containerLogSort.dir === 'asc' ? cmp : -cmp;
      });
    }

    return data;
  }, [containerLogData, containerLogFilters, containerLogSort]);

  // Build container matrix data (status x days range)
  const containerMatrix = useMemo(() => {
    if (!containerLogData) return { statuses: [], dayRanges: ['0-3', '4-7', '8-14', '15-21', '22+'], matrix: {}, totals: {} };

    const dayRanges = ['0-3', '4-7', '8-14', '15-21', '22+'];
    // Exclude "6.Picked-up from yard" status from matrix
    const excludedStatuses = ['6.Picked-up from yard'];
    const statuses = [...new Set(containerLogData.map(r => r.status).filter(s => s && !excludedStatuses.includes(s)))].sort();
    const matrix = {};
    const totals = { byStatus: {}, byDays: {}, total: 0 };

    // Initialize matrix
    statuses.forEach(status => {
      matrix[status] = {};
      dayRanges.forEach(range => {
        matrix[status][range] = 0;
      });
      totals.byStatus[status] = 0;
    });
    dayRanges.forEach(range => {
      totals.byDays[range] = 0;
    });

    // Fill matrix (only count non-excluded statuses)
    containerLogData.forEach(row => {
      const status = row.status;
      if (excludedStatuses.includes(status)) return; // Skip excluded statuses
      const range = getDaysRange(row.days_on_site);
      if (status && matrix[status]) {
        matrix[status][range]++;
        totals.byStatus[status]++;
        totals.byDays[range]++;
        totals.total++;
      }
    });

    return { statuses, dayRanges, matrix, totals };
  }, [containerLogData]);

  // Apply routing overrides to MABD matrix data
  // Overrides are applied to poDetails AND groups (Table 1) is recalculated
  const processedMabdMatrix = useMemo(() => {
    if (!mabdMatrix) return null;

    const overridesByPO = routingOverrides?.byPO || {};
    const hasOverrides = Object.keys(overridesByPO).length > 0;

    // Process poDetails to add override info and override appointment dates
    const processedPoDetails = (mabdMatrix.poDetails || []).map(row => {
      const override = overridesByPO[row.po_no];
      if (override) {
        // Format override date to match TMS format (MM/DD/YYYY)
        let formattedOverrideDate = override.override_appt_date;
        if (formattedOverrideDate && !formattedOverrideDate.includes('/')) {
          // Convert YYYY-MM-DD to MM/DD/YYYY
          const parts = formattedOverrideDate.split('-');
          if (parts.length === 3) {
            formattedOverrideDate = `${parts[1]}/${parts[2]}/${parts[0]}`;
          }
        }
        return {
          ...row,
          has_override: true,
          original_appointment_date: row.appointment_date,
          override_reason: override.reason,
          override_responsibility: override.responsibility,
          override_note: override.note,
          // Replace appointment_date with override for display and matrix calc
          appointment_date: formattedOverrideDate || row.appointment_date
        };
      }
      return { ...row, has_override: false };
    });

    // If no overrides, return original data
    if (!hasOverrides) {
      return {
        ...mabdMatrix,
        poDetails: processedPoDetails,
        hasOverrides: false
      };
    }

    // Rebuild groups (Table 1) matrix with override dates
    // This matches the backend structure: { columns, data, summary }
    const rebuildGroups = () => {
      const groupNames = ['InMocean', 'DSDC', 'BWI'];
      const groups = {};

      // Helper to parse date for sorting (MM/DD/YYYY format)
      const parseDateForSort = (dateStr) => {
        if (!dateStr || dateStr === 'NOT_ROUTED' || dateStr === 'Pending') return null;
        const parts = dateStr.split('/');
        if (parts.length >= 2) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const year = parts.length === 3 ? parseInt(parts[2], 10) : 2025;
          return new Date(year, month - 1, day).getTime();
        }
        return null;
      };

      groupNames.forEach(groupName => {
        const groupPOs = processedPoDetails.filter(d => d.group_name === groupName);

        // Collect all appointment dates
        const appointmentDates = new Set();
        groupPOs.forEach(po => {
          const apptDate = po.appointment_date;
          if (apptDate && apptDate !== 'NOT_ROUTED' && apptDate !== '') {
            appointmentDates.add(apptDate);
          }
        });

        // Sort dates chronologically
        const sortedDates = Array.from(appointmentDates).sort((a, b) => {
          const dateA = parseDateForSort(a);
          const dateB = parseDateForSort(b);
          if (dateA === null) return 1;
          if (dateB === null) return -1;
          return dateA - dateB;
        });

        // Build MABD map
        const mabdMap = {};
        groupPOs.forEach(po => {
          const mabdKey = po.mabd || 'No MABD';
          const amount = parseFloat(po.amount) || 0;

          if (!mabdMap[mabdKey]) {
            mabdMap[mabdKey] = { mabd: mabdKey, 'Not Routed': 0, Pending: 0, total: 0 };
            sortedDates.forEach(d => mabdMap[mabdKey][d] = 0);
          }

          if (po.appointment_date === 'NOT_ROUTED') {
            mabdMap[mabdKey]['Not Routed'] += amount;
          } else if (!po.appointment_date || po.appointment_date === '') {
            mabdMap[mabdKey].Pending += amount;
          } else {
            // Ensure column exists (might be new override date)
            if (mabdMap[mabdKey][po.appointment_date] === undefined) {
              mabdMap[mabdKey][po.appointment_date] = 0;
            }
            mabdMap[mabdKey][po.appointment_date] += amount;
          }
          mabdMap[mabdKey].total += amount;
        });

        // Sort MABD rows by date (MM/DD format)
        const matrixData = Object.values(mabdMap).sort((a, b) => {
          if (!a.mabd || a.mabd === 'No MABD') return 1;
          if (!b.mabd || b.mabd === 'No MABD') return -1;
          const [aMonth, aDay] = a.mabd.split('/').map(Number);
          const [bMonth, bDay] = b.mabd.split('/').map(Number);
          if (aMonth !== bMonth) return aMonth - bMonth;
          return aDay - bDay;
        });

        // Re-sort columns to include any new override dates
        const allDates = new Set(sortedDates);
        matrixData.forEach(row => {
          Object.keys(row).forEach(key => {
            if (key !== 'mabd' && key !== 'Not Routed' && key !== 'Pending' && key !== 'total' && row[key] > 0) {
              allDates.add(key);
            }
          });
        });
        const finalSortedDates = Array.from(allDates).sort((a, b) => {
          const dateA = parseDateForSort(a);
          const dateB = parseDateForSort(b);
          if (dateA === null) return 1;
          if (dateB === null) return -1;
          return dateA - dateB;
        });

        const columns = ['Not Routed', 'Pending', ...finalSortedDates];

        groups[groupName] = {
          columns,
          data: matrixData,
          summary: {
            totalMABDs: matrixData.length,
            totalAppointmentDates: finalSortedDates.length,
            grandTotal: matrixData.reduce((sum, row) => sum + row.total, 0)
          }
        };
      });

      return groups;
    };

    console.log('Rebuilding Table 1 matrix with', Object.keys(overridesByPO).length, 'overrides');

    return {
      ...mabdMatrix,
      poDetails: processedPoDetails,
      groups: rebuildGroups(),
      hasOverrides: true
    };
  }, [mabdMatrix, routingOverrides]);

  // Load data based on active section and tab
  useEffect(() => {
    if (activeSection === 'tms' && activeTab === 'walmart' && !mabdMatrix) {
      loadWalmartData();
    } else if (activeSection === 'tms' && activeTab === 'target' && targetSubTab === 'dims' && !dimsData) {
      // CHA-463 — the dims sub-tab loads its own data; it must be checked BEFORE the routing
      // branch, otherwise opening Target on the dims sub-tab fetches the routing matrix instead.
      loadDimsData();
    } else if (activeSection === 'tms' && activeTab === 'target' && targetSubTab === 'routing' && !targetData) {
      loadTargetData();
    } else if (activeSection === 'warehouse' && activeTab === 'bl' && !blLogData) {
      loadBLLogData();
    } else if (activeSection === 'warehouse' && activeTab === 'lap' && !lapLogData) {
      loadLAPLogData();
    } else if (activeSection === 'warehouse' && activeTab === 'billing' && billingTab === 'storage' && !storageData) {
      loadStorageData();
    } else if (activeSection === 'warehouse' && activeTab === 'billing' && billingTab === 'inbound' && !inboundData) {
      loadInboundData();
    } else if (activeSection === 'warehouse' && activeTab === 'billing' && billingTab === 'outbound' && !outboundData) {
      loadOutboundData();
    }
  }, [activeSection, activeTab, billingTab, targetSubTab]);

  const loadStorageData = async (minDays) => {
    setStorageLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getWarehouseStorageData = httpsCallable(functions, 'getWarehouseStorageData');
      const result = await getWarehouseStorageData({ minDays: minDays || 0 });
      setStorageData(result.data.rows || []);
      console.log('Warehouse storage data loaded:', result.data.rows);
    } catch (err) {
      console.error('Error loading warehouse storage data:', err);
      setError(err.message);
    } finally {
      setStorageLoading(false);
    }
  };

  const loadInboundData = async (from, to) => {
    setInboundLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getWarehouseInboundData = httpsCallable(functions, 'getWarehouseInboundData');
      const result = await getWarehouseInboundData({ fromDate: from || inboundFrom, toDate: to || inboundTo });
      setInboundData(result.data.rows || []);
      console.log('Warehouse inbound data loaded:', result.data.rows);
    } catch (err) {
      console.error('Error loading warehouse inbound data:', err);
      setError(err.message);
    } finally {
      setInboundLoading(false);
    }
  };

  const loadOutboundData = async (from, to) => {
    setOutboundLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getWarehouseOutboundData = httpsCallable(functions, 'getWarehouseOutboundData');
      const result = await getWarehouseOutboundData({ fromDate: from || outboundFrom, toDate: to || outboundTo });
      setOutboundData(result.data.rows || []);
      console.log('Warehouse outbound data loaded:', result.data.rows);
    } catch (err) {
      console.error('Error loading warehouse outbound data:', err);
      setError(err.message);
    } finally {
      setOutboundLoading(false);
    }
  };

  const loadWalmartData = async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getWalmartMABDMatrix = httpsCallable(functions, 'getWalmartMABDMatrix', { timeout: 150000 });
      const getRoutingOverrides = httpsCallable(functions, 'getRoutingOverrides');

      // Load both in parallel
      const [matrixResult, overridesResult] = await Promise.all([
        getWalmartMABDMatrix(),
        getRoutingOverrides({ context: 'logistics' })
      ]);

      setMabdMatrix(matrixResult.data);
      setRoutingOverrides(overridesResult.data || { byPO: {}, byPickTicket: {}, byLoad: {} });
      console.log('MABD Matrix loaded:', matrixResult.data);
      console.log('Routing overrides loaded:', overridesResult.data);
      console.log('Style Details keys:', Object.keys(matrixResult.data?.styleDetails || {}).slice(0, 10));
    } catch (err) {
      console.error('Error loading Walmart MABD matrix:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // CHA-463 — load the SP27 dim coverage: what we can send Target, and what is still missing.
  const loadDimsData = async () => {
    setDimsLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getTargetDimUploadList = httpsCallable(functions, 'getTargetDimUploadList', { timeout: 330000 });
      const result = await getTargetDimUploadList(tenantArg());
      setDimsData(result.data);
      return true;
    } catch (err) {
      console.error('Error loading Target dim coverage:', err);
      setError(err.message);
      return false;
    } finally {
      setDimsLoading(false);
    }
  };

  // CHA-463 — rebuild the dim library from every Matson week loaded so far, then refresh
  // coverage. Separate from loadDimsData because it recomputes rather than reads: the
  // carton-weighted mode that picks each item's spec is derived across all weeks, so a new
  // upload only changes the answer once this has run.
  const rebuildDimLibrary = async () => {
    setDimsRebuilding(true);
    setDimsRebuildResult(null);
    setError(null);
    try {
      const functions = getFunctions();
      const buildMatsonDimLibrary = httpsCallable(functions, 'buildMatsonDimLibrary', { timeout: 330000 });
      const result = await buildMatsonDimLibrary(tenantArg());
      setDimsRebuildResult(result.data);
      await loadDimsData();
    } catch (err) {
      console.error('Error rebuilding the dim library:', err);
      setError(err.message);
    } finally {
      setDimsRebuilding(false);
    }
  };

  // Load Target Routing data
  const loadTargetData = async () => {
    setTargetLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getTargetRoutingMatrix = httpsCallable(functions, 'getTargetRoutingMatrix');
      const result = await getTargetRoutingMatrix();
      setTargetData(result.data);
      console.log('Target Routing data loaded:', result.data);
    } catch (err) {
      console.error('Error loading Target routing data:', err);
      setError(err.message);
    } finally {
      setTargetLoading(false);
    }
  };

  // Load BL Log data from BigQuery
  const loadBLLogData = async () => {
    setBlLogLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getBLLogData = httpsCallable(functions, 'getBLLogData');
      const result = await getBLLogData();
      setBlLogData(result.data.rows || []);
      setBlLogLastSync(result.data.lastSync);
      console.log('BL Log loaded:', result.data.rows?.length, 'rows');
    } catch (err) {
      console.error('Error loading BL Log:', err);
      setError(err.message);
    } finally {
      setBlLogLoading(false);
    }
  };

  // Sync BL Log from Google Sheets
  const syncBLLog = async () => {
    setBlLogSyncing(true);
    setError(null);
    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsWithTablePerTab');
      await syncFunction({
        sheetId: ROUTING_SHEET_ID,
        tabName: 'BL'
      });
      // Reload data after sync
      await loadBLLogData();
    } catch (err) {
      console.error('Error syncing BL Log:', err);
      setError(err.message);
    } finally {
      setBlLogSyncing(false);
    }
  };

  // Load LAP Log data from BigQuery
  const loadLAPLogData = async () => {
    setLapLogLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getLAPLogData = httpsCallable(functions, 'getLAPLogData');
      const result = await getLAPLogData();
      setLapLogData(result.data.rows || []);
      setLapLogLastSync(result.data.lastSync);
      console.log('LAP Log loaded:', result.data.rows?.length, 'rows');
    } catch (err) {
      console.error('Error loading LAP Log:', err);
      setError(err.message);
    } finally {
      setLapLogLoading(false);
    }
  };

  // Sync LAP Log from Google Sheets
  const syncLAPLog = async () => {
    setLapLogSyncing(true);
    setError(null);
    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsWithTablePerTab');
      await syncFunction({
        sheetId: ROUTING_SHEET_ID,
        tabName: 'LAP Updates'
      });
      // Reload data after sync
      await loadLAPLogData();
    } catch (err) {
      console.error('Error syncing LAP Log:', err);
      setError(err.message);
    } finally {
      setLapLogSyncing(false);
    }
  };

  // Load Container Log data
  const loadContainerLogData = async () => {
    setContainerLogLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getContainerLogData = httpsCallable(functions, 'getContainerLogData');
      const result = await getContainerLogData();
      setContainerLogData(result.data.rows || []);
      setContainerLogLastSync(result.data.lastSync);
      console.log('Container Log loaded:', result.data.rows?.length, 'rows');
    } catch (err) {
      console.error('Error loading Container Log:', err);
      setError(err.message);
    } finally {
      setContainerLogLoading(false);
    }
  };

  // Load routing notes
  const loadRoutingNotes = async (searchType = null, searchValue = null) => {
    setNotesLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getRoutingNotes = httpsCallable(functions, 'getRoutingNotes');
      const result = await getRoutingNotes({
        search_type: searchType,
        search_value: searchValue,
        context: 'logistics',
        limit: 50
      });
      setRoutingNotes(result.data.notes || []);
      console.log('Routing notes loaded:', result.data.notes?.length, 'notes');
    } catch (err) {
      console.error('Error loading routing notes:', err);
      setError(err.message);
    } finally {
      setNotesLoading(false);
    }
  };

  // Save routing note (add or update)
  const saveRoutingNote = async () => {
    setNotesSaving(true);
    setError(null);
    try {
      const functions = getFunctions();

      if (editingNote) {
        // Update existing note
        const updateRoutingNote = httpsCallable(functions, 'updateRoutingNote');
        await updateRoutingNote({
          noteId: editingNote.id,
          ...noteForm
        });
        console.log('Note updated:', editingNote.id);
      } else {
        // Add new note
        const addRoutingNote = httpsCallable(functions, 'addRoutingNote');
        await addRoutingNote({
          ...noteForm,
          context: 'logistics'
        });
        console.log('Note added');
      }

      // Reset form and reload notes
      resetNoteForm();
      await loadRoutingNotes();
    } catch (err) {
      console.error('Error saving routing note:', err);
      setError(err.message);
    } finally {
      setNotesSaving(false);
    }
  };

  // Delete routing note
  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    setNotesSaving(true);
    try {
      const functions = getFunctions();
      const deleteRoutingNote = httpsCallable(functions, 'deleteRoutingNote');
      await deleteRoutingNote({ noteId });
      console.log('Note deleted:', noteId);
      await loadRoutingNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      setError(err.message);
    } finally {
      setNotesSaving(false);
    }
  };

  // Reset note form
  const resetNoteForm = () => {
    setEditingNote(null);
    setNoteForm({
      lookup_type: 'po',
      lookup_value: '',
      original_appt_date: '',
      override_appt_date: '',
      mabd: '',
      reason: '',
      responsibility: '',
      note: ''
    });
  };

  // Edit existing note
  const editNote = (note) => {
    setEditingNote(note);
    setNoteForm({
      lookup_type: note.lookup_type || 'po',
      lookup_value: note.lookup_value || '',
      original_appt_date: note.original_appt_date || '',
      override_appt_date: note.override_appt_date || '',
      mabd: note.mabd || '',
      reason: note.reason || '',
      responsibility: note.responsibility || '',
      note: note.note || ''
    });
  };

  // Search for order data and populate form
  const handleNotesSearch = async () => {
    if (!notesSearch.value.trim()) {
      setSearchedOrder(null);
      return;
    }

    setNotesSearching(true);
    setError(null);
    try {
      const functions = getFunctions();
      const searchOrderForNote = httpsCallable(functions, 'searchOrderForNote');
      const result = await searchOrderForNote({
        search_type: notesSearch.type,
        search_value: notesSearch.value.trim()
      });

      if (result.data.found) {
        setSearchedOrder(result.data.orderData);

        // If there's an existing note, load it for editing
        if (result.data.existingNote) {
          setEditingNote(result.data.existingNote);
          setNoteForm({
            lookup_type: result.data.existingNote.lookup_type || notesSearch.type,
            lookup_value: result.data.existingNote.lookup_value || notesSearch.value.trim(),
            original_appt_date: result.data.existingNote.original_appt_date || result.data.orderData.appointment_date || '',
            override_appt_date: result.data.existingNote.override_appt_date || '',
            mabd: result.data.existingNote.mabd || result.data.orderData.mabd || '',
            reason: result.data.existingNote.reason || '',
            responsibility: result.data.existingNote.responsibility || '',
            note: result.data.existingNote.note || ''
          });
        } else {
          // Pre-populate form with order data for new note
          setEditingNote(null);
          setNoteForm({
            lookup_type: notesSearch.type,
            lookup_value: notesSearch.value.trim(),
            original_appt_date: result.data.orderData.appointment_date || '',
            override_appt_date: '',
            mabd: result.data.orderData.mabd || '',
            reason: '',
            responsibility: '',
            note: ''
          });
        }
        console.log('Order found:', result.data.orderData);
      } else {
        setSearchedOrder(null);
        setError(result.data.message || 'Order not found');
      }
    } catch (err) {
      console.error('Error searching order:', err);
      setError(err.message);
      setSearchedOrder(null);
    } finally {
      setNotesSearching(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value?.toFixed(0) || 0}`;
  };

  // Format date for display (MM/DD from various formats)
  const formatDateHeader = (dateStr) => {
    if (!dateStr || dateStr === 'Pending' || dateStr === 'Not Routed') return dateStr;
    // Handle MM/DD/YY or MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length >= 2) {
      return `${parts[0]}/${parts[1]}`;
    }
    return dateStr;
  };

  // Style 1: Clean Minimal Design
  const renderMinimalTable = (groupData) => {
    if (!groupData || !groupData.data || groupData.data.length === 0) {
      return <div className="p-8 text-center text-gray-400">No data available</div>;
    }

    return (
      <div className="overflow-x-auto p-4">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="py-3 pr-6 text-left text-sm font-semibold text-gray-900">MABD</th>
              {groupData.columns.map((col) => (
                <th key={col} className={`py-3 px-4 text-right text-sm font-semibold ${col === 'Pending' ? 'text-orange-600' : 'text-gray-900'}`}>
                  {formatDateHeader(col)}
                </th>
              ))}
              <th className="py-3 pl-6 text-right text-sm font-semibold text-gray-900">Total</th>
            </tr>
          </thead>
          <tbody>
            {groupData.data.map((row, idx) => (
              <tr key={idx} className="border-b border-gray-100">
                <td className="py-3 pr-6 text-sm font-medium text-gray-900">{row.mabd || '-'}</td>
                {groupData.columns.map((col) => {
                  const value = row[col] || 0;
                  return (
                    <td key={col} className={`py-3 px-4 text-right text-sm ${value > 0 ? (col === 'Pending' ? 'text-orange-600 font-medium' : 'text-gray-700') : 'text-gray-300'}`}>
                      {value > 0 ? formatCurrency(value) : '—'}
                    </td>
                  );
                })}
                <td className="py-3 pl-6 text-right text-sm font-semibold text-gray-900">{formatCurrency(row.total || 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300">
              <td className="py-3 pr-6 text-sm font-bold text-gray-900">Total</td>
              {groupData.columns.map((col) => {
                const colTotal = groupData.data.reduce((sum, row) => sum + (row[col] || 0), 0);
                return (
                  <td key={col} className={`py-3 px-4 text-right text-sm font-bold ${col === 'Pending' ? 'text-orange-600' : 'text-gray-900'}`}>
                    {colTotal > 0 ? formatCurrency(colTotal) : '—'}
                  </td>
                );
              })}
              <td className="py-3 pl-6 text-right text-sm font-bold text-blue-600">{formatCurrency(groupData.summary?.grandTotal || 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  // Style 2: Notion-style Table with better lines
  const renderNotionTable = (groupData, sortDir = 'asc', onSortToggle = null) => {
    if (!groupData || !groupData.data || groupData.data.length === 0) {
      return <div className="p-8 text-center text-gray-400">No data available</div>;
    }

    // Sort data by MABD
    const sortedData = [...groupData.data].sort((a, b) => {
      // Parse MM/DD format for comparison
      const parseDate = (mabd) => {
        if (!mabd || mabd === '-') return sortDir === 'asc' ? Infinity : -Infinity;
        const [month, day] = mabd.split('/').map(Number);
        // Assume current year, adjust if needed
        return month * 100 + day;
      };
      const dateA = parseDate(a.mabd);
      const dateB = parseDate(b.mabd);
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return (
      <div className="overflow-auto max-h-[200px]" style={{ maxWidth: '100%' }}>
        <table className="border-collapse text-xs" style={{ minWidth: '500px' }}>
          <thead className="sticky top-0 z-10">
            <tr className="border-b-2 border-gray-300">
              <th
                onClick={onSortToggle}
                className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 bg-gray-50 whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
              >
                <span className="flex items-center gap-1">
                  MABD
                  {sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </span>
              </th>
              {groupData.columns.map((col, idx) => (
                <th
                  key={col}
                  className={`px-2 py-1.5 text-right text-xs font-semibold border-l border-gray-200 whitespace-nowrap ${
                    col === 'Not Routed'
                      ? 'text-red-700 bg-red-50'
                      : col === 'Pending'
                        ? 'text-amber-700 bg-amber-50'
                        : 'text-gray-600 bg-gray-50'
                  }`}
                >
                  {formatDateHeader(col)}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700 bg-gray-100 border-l-2 border-gray-300 whitespace-nowrap sticky right-0 z-20">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((row, idx) => (
              <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                <td className="px-2 py-1.5 text-xs text-gray-900 font-medium bg-white whitespace-nowrap">
                  {row.mabd || '-'}
                </td>
                {groupData.columns.map((col) => {
                  const value = row[col] || 0;
                  const isSelected = selectedCell?.mabd === row.mabd && selectedCell?.appointmentDate === col;
                  return (
                    <td
                      key={col}
                      onClick={() => { if (value > 0) { setSelectedPendingCell(null); setSelectedCell({ mabd: row.mabd, appointmentDate: col }); } }}
                      className={`px-2 py-1.5 text-xs text-right border-l border-gray-100 whitespace-nowrap ${
                        value > 0 ? 'cursor-pointer hover:bg-blue-100' : ''
                      } ${
                        isSelected ? 'ring-2 ring-blue-500 bg-blue-100' : ''
                      } ${
                        col === 'Not Routed'
                          ? value > 0
                            ? 'bg-red-50 text-red-800 font-medium'
                            : 'bg-red-50/30 text-gray-300'
                          : col === 'Pending'
                            ? value > 0
                              ? 'bg-amber-50 text-amber-800 font-medium'
                              : 'bg-amber-50/30 text-gray-300'
                          : value > 0
                            ? 'text-gray-800'
                            : 'text-gray-300'
                      }`}
                    >
                      {value > 0 ? formatCurrency(value) : '—'}
                    </td>
                  );
                })}
                {/* Row total - clickable to see all POs for this MABD - STICKY */}
                <td
                  onClick={() => { if (row.total > 0) { setSelectedPendingCell(null); setSelectedCell({ mabd: row.mabd, appointmentDate: '_ALL_' }); } }}
                  className={`px-2 py-1.5 text-xs text-right font-semibold text-gray-900 bg-gray-50 border-l-2 border-gray-200 whitespace-nowrap sticky right-0 ${
                    row.total > 0 ? 'cursor-pointer hover:bg-blue-100' : ''
                  } ${
                    selectedCell?.mabd === row.mabd && selectedCell?.appointmentDate === '_ALL_' ? 'ring-2 ring-blue-500 bg-blue-100' : ''
                  }`}
                >
                  {formatCurrency(row.total || 0)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-10">
            <tr className="border-t-2 border-gray-300 bg-gray-100">
              <td className="px-2 py-1.5 text-xs font-bold text-gray-900">Total</td>
              {groupData.columns.map((col) => {
                const colTotal = groupData.data.reduce((sum, row) => sum + (row[col] || 0), 0);
                const isSelected = selectedCell?.mabd === '_ALL_' && selectedCell?.appointmentDate === col;
                return (
                  <td
                    key={col}
                    onClick={() => { if (colTotal > 0) { setSelectedPendingCell(null); setSelectedCell({ mabd: '_ALL_', appointmentDate: col }); } }}
                    className={`px-2 py-1.5 text-xs text-right font-bold border-l border-gray-200 whitespace-nowrap ${
                      colTotal > 0 ? 'cursor-pointer hover:bg-blue-200' : ''
                    } ${
                      isSelected ? 'ring-2 ring-blue-500 bg-blue-200' : ''
                    } ${
                      col === 'Not Routed'
                        ? 'text-red-800 bg-red-100'
                        : col === 'Pending'
                          ? 'text-amber-800 bg-amber-100'
                          : 'text-gray-900'
                    }`}
                  >
                    {colTotal > 0 ? formatCurrency(colTotal) : '—'}
                  </td>
                );
              })}
              {/* Grand total - clickable to see ALL POs - STICKY */}
              <td
                onClick={() => { if (groupData.summary?.grandTotal > 0) { setSelectedPendingCell(null); setSelectedCell({ mabd: '_ALL_', appointmentDate: '_ALL_' }); } }}
                className={`px-2 py-1.5 text-xs text-right font-bold text-blue-800 bg-blue-100 border-l-2 border-gray-300 whitespace-nowrap sticky right-0 z-20 ${
                  groupData.summary?.grandTotal > 0 ? 'cursor-pointer hover:bg-blue-200' : ''
                } ${
                  selectedCell?.mabd === '_ALL_' && selectedCell?.appointmentDate === '_ALL_' ? 'ring-2 ring-blue-500 bg-blue-200' : ''
                }`}
              >
                {formatCurrency(groupData.summary?.grandTotal || 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  // Render Pending Matrix (Table 2) - MABD vs Pickup Window
  const renderPendingMatrix = (groupData, sortDir = 'asc', onSortToggle = null) => {
    if (!groupData || !groupData.data || groupData.data.length === 0) {
      return <div className="p-4 text-center text-gray-400 text-xs">No pending orders</div>;
    }

    // Handler to select cell and clear Table 1 selection
    const handlePendingCellClick = (mabd, pickupWindow) => {
      setSelectedCell(null); // Clear Table 1 selection
      setSelectedPendingCell({ mabd, pickupWindow });
    };

    // Sort data by MABD
    const sortedData = [...groupData.data].sort((a, b) => {
      const parseDate = (mabd) => {
        if (!mabd || mabd === '-') return sortDir === 'asc' ? Infinity : -Infinity;
        const [month, day] = mabd.split('/').map(Number);
        return month * 100 + day;
      };
      const dateA = parseDate(a.mabd);
      const dateB = parseDate(b.mabd);
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return (
      <div className="overflow-auto max-h-[200px]" style={{ maxWidth: '100%' }}>
        <table className="border-collapse text-xs" style={{ minWidth: '300px' }}>
          <thead className="sticky top-0 z-10">
            <tr className="border-b-2 border-gray-300">
              <th
                onClick={onSortToggle}
                className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 bg-gray-50 whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
              >
                <span className="flex items-center gap-1">
                  MABD
                  {sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </span>
              </th>
              {groupData.columns.map((col) => (
                <th
                  key={col}
                  className="px-2 py-1.5 text-right text-xs font-semibold text-amber-700 bg-amber-50 border-l border-gray-200 whitespace-nowrap"
                >
                  {formatDateHeader(col)}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-700 bg-gray-100 border-l-2 border-gray-300 whitespace-nowrap sticky right-0 z-20">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((row, idx) => (
              <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                <td className="px-2 py-1.5 text-xs text-gray-900 font-medium bg-white whitespace-nowrap">
                  {row.mabd || '-'}
                </td>
                {groupData.columns.map((col) => {
                  const value = row[col] || 0;
                  const isSelected = selectedPendingCell?.mabd === row.mabd && selectedPendingCell?.pickupWindow === col;
                  return (
                    <td
                      key={col}
                      onClick={() => value > 0 && handlePendingCellClick(row.mabd, col)}
                      className={`px-2 py-1.5 text-xs text-right border-l border-gray-100 whitespace-nowrap ${
                        value > 0 ? 'cursor-pointer hover:bg-blue-100' : ''
                      } ${
                        isSelected ? 'ring-2 ring-blue-500 bg-blue-100' : ''
                      } ${
                        value > 0
                          ? 'bg-amber-50 text-amber-800 font-medium'
                          : 'bg-amber-50/30 text-gray-300'
                      }`}
                    >
                      {value > 0 ? formatCurrency(value) : '—'}
                    </td>
                  );
                })}
                {/* Row total - clickable - STICKY */}
                <td
                  onClick={() => row.total > 0 && handlePendingCellClick(row.mabd, '_ALL_')}
                  className={`px-2 py-1.5 text-xs text-right font-semibold text-gray-900 bg-gray-50 border-l-2 border-gray-200 whitespace-nowrap sticky right-0 ${
                    row.total > 0 ? 'cursor-pointer hover:bg-blue-100' : ''
                  } ${
                    selectedPendingCell?.mabd === row.mabd && selectedPendingCell?.pickupWindow === '_ALL_' ? 'ring-2 ring-blue-500 bg-blue-100' : ''
                  }`}
                >
                  {formatCurrency(row.total || 0)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-10">
            <tr className="border-t-2 border-gray-300 bg-gray-100">
              <td className="px-2 py-1.5 text-xs font-bold text-gray-900">Total</td>
              {groupData.columns.map((col) => {
                const colTotal = groupData.data.reduce((sum, row) => sum + (row[col] || 0), 0);
                const isSelected = selectedPendingCell?.mabd === '_ALL_' && selectedPendingCell?.pickupWindow === col;
                return (
                  <td
                    key={col}
                    onClick={() => colTotal > 0 && handlePendingCellClick('_ALL_', col)}
                    className={`px-2 py-1.5 text-xs text-right font-bold border-l border-gray-200 whitespace-nowrap ${
                      colTotal > 0 ? 'cursor-pointer hover:bg-blue-200' : ''
                    } ${
                      isSelected ? 'ring-2 ring-blue-500 bg-blue-200' : ''
                    } text-amber-800 bg-amber-100`}
                  >
                    {colTotal > 0 ? formatCurrency(colTotal) : '—'}
                  </td>
                );
              })}
              {/* Grand total - clickable - STICKY */}
              <td
                onClick={() => groupData.summary?.grandTotal > 0 && handlePendingCellClick('_ALL_', '_ALL_')}
                className={`px-2 py-1.5 text-xs text-right font-bold text-blue-800 bg-blue-100 border-l-2 border-gray-300 whitespace-nowrap sticky right-0 z-20 ${
                  groupData.summary?.grandTotal > 0 ? 'cursor-pointer hover:bg-blue-200' : ''
                } ${
                  selectedPendingCell?.mabd === '_ALL_' && selectedPendingCell?.pickupWindow === '_ALL_' ? 'ring-2 ring-blue-500 bg-blue-200' : ''
                }`}
              >
                {formatCurrency(groupData.summary?.grandTotal || 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  // Style 3: Spreadsheet-style
  const renderSpreadsheetTable = (groupData) => {
    if (!groupData || !groupData.data || groupData.data.length === 0) {
      return <div className="p-8 text-center text-gray-400">No data available</div>;
    }

    return (
      <div className="overflow-x-auto border border-gray-300">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-gray-200">
              <th className="px-2 py-1.5 text-left font-semibold text-gray-800 border border-gray-300 sticky left-0 bg-gray-200 z-10">
                MABD
              </th>
              {groupData.columns.map((col) => (
                <th key={col} className={`px-2 py-1.5 text-center font-semibold border border-gray-300 min-w-[70px] ${col === 'Pending' ? 'bg-yellow-200 text-yellow-800' : 'text-gray-800'}`}>
                  {formatDateHeader(col)}
                </th>
              ))}
              <th className="px-2 py-1.5 text-center font-semibold text-gray-800 border border-gray-300 bg-blue-100">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {groupData.data.map((row, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className={`px-2 py-1.5 font-medium text-gray-900 border border-gray-300 sticky left-0 z-10 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  {row.mabd || '-'}
                </td>
                {groupData.columns.map((col) => {
                  const value = row[col] || 0;
                  return (
                    <td key={col} className={`px-2 py-1.5 text-right border border-gray-300 ${col === 'Pending' ? 'bg-yellow-50' : ''} ${value > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                      {value > 0 ? formatCurrency(value) : '-'}
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right font-semibold text-gray-900 border border-gray-300 bg-blue-50">
                  {formatCurrency(row.total || 0)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-200 font-bold">
              <td className="px-2 py-1.5 text-gray-900 border border-gray-300 sticky left-0 bg-gray-200 z-10">Total</td>
              {groupData.columns.map((col) => {
                const colTotal = groupData.data.reduce((sum, row) => sum + (row[col] || 0), 0);
                return (
                  <td key={col} className={`px-2 py-1.5 text-right border border-gray-300 ${col === 'Pending' ? 'bg-yellow-200 text-yellow-800' : 'text-gray-900'}`}>
                    {colTotal > 0 ? formatCurrency(colTotal) : '-'}
                  </td>
                );
              })}
              <td className="px-2 py-1.5 text-right text-blue-800 border border-gray-300 bg-blue-200">
                {formatCurrency(groupData.summary?.grandTotal || 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  // Style 4: Card-based Layout
  const renderCardTable = (groupData) => {
    if (!groupData || !groupData.data || groupData.data.length === 0) {
      return <div className="p-8 text-center text-gray-400">No data available</div>;
    }

    return (
      <div className="p-4 space-y-3">
        {groupData.data.map((row, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-gray-900">{row.mabd || '-'}</span>
                <span className="text-xs text-gray-500 uppercase">MABD</span>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-blue-600">{formatCurrency(row.total || 0)}</span>
                <span className="text-xs text-gray-500 ml-1">total</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {groupData.columns.map((col) => {
                const value = row[col] || 0;
                if (value <= 0) return null;
                return (
                  <div key={col} className={`px-3 py-1.5 rounded-full text-xs font-medium ${col === 'Pending' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                    <span className="font-normal opacity-75">{formatDateHeader(col)}:</span> {formatCurrency(value)}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {/* Totals Card */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-bold">Grand Total</span>
            <span className="text-2xl font-bold">{formatCurrency(groupData.summary?.grandTotal || 0)}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {groupData.columns.map((col) => {
              const colTotal = groupData.data.reduce((sum, row) => sum + (row[col] || 0), 0);
              if (colTotal <= 0) return null;
              return (
                <div key={col} className={`px-3 py-1.5 rounded-full text-xs font-medium ${col === 'Pending' ? 'bg-amber-400 text-amber-900' : 'bg-white/20 text-white'}`}>
                  <span className="opacity-75">{formatDateHeader(col)}:</span> {formatCurrency(colTotal)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Render Open Orders Table (Table 4) - Open orders (status 'O') by MABD
  const renderOpenOrdersTable = (openOrdersData, sortDir = 'asc', onSortToggle = null) => {
    if (!openOrdersData || !openOrdersData.data || openOrdersData.data.length === 0) {
      return <div className="p-4 text-center text-gray-400 text-xs">No open orders</div>;
    }

    // Sort data by MABD
    const sortedData = [...openOrdersData.data].sort((a, b) => {
      const parseDate = (mabd) => {
        if (!mabd || mabd === '-' || mabd === 'No MABD') return sortDir === 'asc' ? Infinity : -Infinity;
        const [month, day] = mabd.split('/').map(Number);
        return month * 100 + day;
      };
      const dateA = parseDate(a.mabd);
      const dateB = parseDate(b.mabd);
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return (
      <div className="overflow-auto max-h-[200px]">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="border-b-2 border-gray-300">
              <th
                onClick={onSortToggle}
                className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none"
              >
                <span className="flex items-center gap-1">
                  MABD
                  {sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </span>
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600 bg-gray-50">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedData.map((row, idx) => (
              <tr
                key={idx}
                onClick={() => {
                  setSelectedOpenMABD(selectedOpenMABD === row.mabd ? null : row.mabd);
                  setSelectedCell(null);
                  setSelectedPendingCell(null);
                  setSelectedPO(null);
                }}
                className={`hover:bg-blue-50/50 transition-colors cursor-pointer ${selectedOpenMABD === row.mabd ? 'bg-blue-100' : ''}`}
              >
                <td className="px-2 py-1.5 text-xs text-gray-900 font-medium">{row.mabd || '-'}</td>
                <td className="px-2 py-1.5 text-xs text-right text-gray-900 font-medium">{formatCurrency(row.total_amount || 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-10">
            <tr className="border-t-2 border-gray-300 bg-gray-100">
              <td className="px-2 py-1.5 text-xs font-bold text-gray-900">Total</td>
              <td className="px-2 py-1.5 text-xs text-right font-bold text-blue-800">{formatCurrency(openOrdersData.summary?.grandTotal || 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  // Table style state
  const [tableStyle, setTableStyle] = useState(2);

  // Render table based on selected style
  const renderMatrixTable = (groupData, groupName, sortDir, onSortToggle) => {
    switch (tableStyle) {
      case 1: return renderMinimalTable(groupData);
      case 2: return renderNotionTable(groupData, sortDir, onSortToggle);
      case 3: return renderSpreadsheetTable(groupData);
      case 4: return renderCardTable(groupData);
      default: return renderMinimalTable(groupData);
    }
  };

  // Render Walmart Routing tab - Notion-style view with group switcher
  const renderWalmartTab = () => {
    const groups = processedMabdMatrix?.groups || {};
    const groupOrder = ['InMocean', 'DSDC', 'BWI'];

    // Styles for each group view tab
    const groupStyles = {
      'InMocean': {
        activeBg: 'bg-blue-600',
        activeText: 'text-white',
        inactiveBg: 'bg-gray-100',
        inactiveText: 'text-gray-600',
        hoverBg: 'hover:bg-blue-50'
      },
      'DSDC': {
        activeBg: 'bg-purple-600',
        activeText: 'text-white',
        inactiveBg: 'bg-gray-100',
        inactiveText: 'text-gray-600',
        hoverBg: 'hover:bg-purple-50'
      },
      'BWI': {
        activeBg: 'bg-green-600',
        activeText: 'text-white',
        inactiveBg: 'bg-gray-100',
        inactiveText: 'text-gray-600',
        hoverBg: 'hover:bg-green-50'
      }
    };

    const activeGroup = groups[activeGroupView];

    return (
      <div className="space-y-4">
        {/* Row 1: Table 1 (60%) | Table 2 (40%) */}
        <div className="grid grid-cols-5 gap-4">
          {/* Table 1: MABD Matrix (60%) */}
          <div className="col-span-3 bg-white rounded-lg shadow">
          {/* View tabs header */}
          <div className="px-3 py-2 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700">Pick Orders by MABD</span>
                <span className="text-xs text-gray-400">|</span>
                <div className="flex gap-1">
                  {groupOrder.map(groupName => {
                    const group = groups[groupName];
                    const styles = groupStyles[groupName];
                    const isActive = activeGroupView === groupName;
                    const total = group?.summary?.grandTotal || 0;

                    return (
                      <button
                        key={groupName}
                        onClick={() => setActiveGroupView(groupName)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                          isActive
                            ? `${styles.activeBg} ${styles.activeText}`
                            : `${styles.inactiveBg} ${styles.inactiveText} ${styles.hoverBg}`
                        }`}
                      >
                        {groupName}
                        {total > 0 && (
                          <span className={`ml-1 ${isActive ? 'opacity-80' : 'opacity-60'}`}>
                            {formatCurrency(total)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Matrix Table for active view */}
          {processedMabdMatrix && processedMabdMatrix.groups ? (
            activeGroup ? (
              renderMatrixTable(activeGroup, activeGroupView, table1SortDir, () => setTable1SortDir(d => d === 'asc' ? 'desc' : 'asc'))
            ) : (
              <div className="p-4 text-center text-gray-500 text-xs">
                No data for {activeGroupView}
              </div>
            )
          ) : (
            <div className="p-4 text-center text-gray-500 text-xs">
              {loading ? 'Loading...' : 'No data available'}
            </div>
          )}
        </div>

          {/* Table 2: Pending by Pickup Window (40%) */}
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-3 py-2 border-b border-gray-200">
              <span className="text-xs font-semibold text-gray-700">Pending by Pickup Window</span>
              <span className="ml-2 text-xs text-gray-500">
                {processedMabdMatrix?.pendingGroups?.[activeGroupView]?.summary?.grandTotal > 0
                  ? formatCurrency(processedMabdMatrix.pendingGroups[activeGroupView].summary.grandTotal)
                  : '—'}
              </span>
            </div>
            {renderPendingMatrix(processedMabdMatrix?.pendingGroups?.[activeGroupView], table2SortDir, () => setTable2SortDir(d => d === 'asc' ? 'desc' : 'asc'))}
          </div>
        </div>

        {/* Row 2: Table 3 (2/3) + Split PO (1/3) */}
        <div className="grid grid-cols-3 gap-4">
          {/* Table 3: PO Details (2/3 width) */}
          <div className="col-span-2 bg-white rounded-lg shadow">
          <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-700">PO Details</span>
              {/* Department filter */}
              <span className="text-xs text-gray-400">|</span>
              <span className="text-xs text-gray-500">Dept:</span>
              <select
                value={selectedDepartment || ''}
                onChange={(e) => setSelectedDepartment(e.target.value || null)}
                className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white"
              >
                <option value="">All</option>
                {[...new Set((processedMabdMatrix?.poDetails || []).map(d => d.department).filter(Boolean))].sort().map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            {(selectedCell || selectedPendingCell) && (
              <span className="text-xs text-gray-500">
                {selectedCell ? (
                  <>MABD: {selectedCell.mabd === '_ALL_' ? 'All' : selectedCell.mabd} | Appt: {selectedCell.appointmentDate === '_ALL_' ? 'All' : selectedCell.appointmentDate}</>
                ) : (
                  <>MABD: {selectedPendingCell.mabd === '_ALL_' ? 'All' : selectedPendingCell.mabd} | Pickup: {selectedPendingCell.pickupWindow === '_ALL_' ? 'All' : selectedPendingCell.pickupWindow} (Pending)</>
                )}
                <button
                  onClick={() => { setSelectedCell(null); setSelectedPendingCell(null); }}
                  className="ml-2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </span>
            )}
          </div>
          <div className="overflow-auto max-h-[200px]">
            {(() => {
              // Filter PO details based on selected cell from Table 1 or Table 2
              const details = processedMabdMatrix?.poDetails || [];
              let filtered;

              if (selectedCell) {
                // Filter from Table 1 selection
                filtered = details.filter(d => {
                  // Must match group
                  if (d.group_name !== activeGroupView) return false;

                  // Filter by MABD (or all if _ALL_)
                  const mabdMatch = selectedCell.mabd === '_ALL_' || d.mabd === selectedCell.mabd;
                  if (!mabdMatch) return false;

                  // Filter by appointment date (or all if _ALL_)
                  if (selectedCell.appointmentDate === '_ALL_') return true;
                  if (selectedCell.appointmentDate === 'Not Routed') return d.appointment_date === 'NOT_ROUTED';
                  if (selectedCell.appointmentDate === 'Pending') return !d.appointment_date || d.appointment_date === '';
                  return d.appointment_date === selectedCell.appointmentDate;
                });
              } else if (selectedPendingCell) {
                // Filter from Table 2 selection (Pending orders only)
                filtered = details.filter(d => {
                  // Must match group
                  if (d.group_name !== activeGroupView) return false;

                  // Must be Pending (no appointment date)
                  if (d.appointment_date && d.appointment_date !== '' && d.appointment_date !== 'NOT_ROUTED') return false;
                  // Exclude Not Routed - Table 2 is only for Pending (in TMS but no appt)
                  if (d.appointment_date === 'NOT_ROUTED') return false;

                  // Filter by MABD (or all if _ALL_)
                  const mabdMatch = selectedPendingCell.mabd === '_ALL_' || d.mabd === selectedPendingCell.mabd;
                  if (!mabdMatch) return false;

                  // For pickup window, we'd need to add that to PO details - for now just filter by MABD
                  return true;
                });
              } else {
                filtered = details.filter(d => d.group_name === activeGroupView).slice(0, 50);
              }

              // Apply department filter
              if (selectedDepartment) {
                filtered = filtered.filter(d => d.department === selectedDepartment);
              }

              if (filtered.length === 0) {
                return (
                  <div className="p-4 text-center text-gray-400 text-xs">
                    {(selectedCell || selectedPendingCell) ? 'No POs for selection' : 'Click a cell in Table 1 or 2 to filter'}
                  </div>
                );
              }

              return (
                <table className="w-full text-xs border-collapse" style={{ minWidth: '1000px' }}>
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">PO</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Dept</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Pick</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">WH</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Amount</th>
                      <th className="px-2 py-1.5 text-center font-semibold text-gray-600">Event Code</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Cases</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Pallets</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">MABD</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Appt Date</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Carrier</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Load Dest</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">PO Dest</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Mode</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((row, idx) => (
                      <tr key={idx} className={`hover:bg-gray-50 ${selectedPO === row.po_no ? 'bg-blue-50' : ''} ${row.has_override ? 'bg-amber-50' : ''}`}>
                        <td className="px-2 py-1.5">
                          <button
                            onClick={() => {
                              const newPO = selectedPO === row.po_no ? null : row.po_no;
                              console.log('Selected PO:', newPO, 'Style data:', processedMabdMatrix?.styleDetails?.[newPO]);
                              setSelectedPO(newPO);
                            }}
                            className={`font-medium hover:underline ${selectedPO === row.po_no ? 'text-blue-600' : 'text-gray-900'}`}
                          >
                            {row.po_no}
                            {row.has_override && <span className="ml-1 text-amber-600" title="Has routing override">*</span>}
                          </button>
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">{row.department || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.pick_number || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.warehouse || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-gray-900">{formatCurrency(row.amount || 0)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            row.event_code_display === 'Not Routed'
                              ? 'bg-red-100 text-red-700'
                              : row.event_code_display === 'Routed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}>{row.event_code_display || '—'}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right text-gray-600">{row.cases || '—'}</td>
                        <td className="px-2 py-1.5 text-right text-gray-600">{row.pallets || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.mabd || '—'}</td>
                        <td className="px-2 py-1.5">
                          {row.has_override ? (
                            <span className="text-amber-700 font-medium" title={`Original: ${row.original_appointment_date || 'N/A'}\nReason: ${row.override_reason || 'N/A'}\nResponsibility: ${row.override_responsibility || 'N/A'}`}>
                              {row.appointment_date || '—'}
                              <span className="ml-1 text-xs text-amber-500">(ovr)</span>
                            </span>
                          ) : (
                            <span className="text-gray-600">
                              {row.appointment_date === 'NOT_ROUTED' ? 'Not Routed' : row.appointment_date || '—'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">{row.carrier_name || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.load_dest_type || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.po_dest || '—'}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.mode || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
          </div>

          {/* Split PO (1/3 width) */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">Split PO</span>
              {processedMabdMatrix?.splitPOs?.[activeGroupView]?.length > 0 && (
                <span className="text-xs text-gray-500">
                  {processedMabdMatrix.splitPOs[activeGroupView].length} POs
                </span>
              )}
            </div>
            <div className="overflow-auto max-h-[200px]">
              {(() => {
                const splitPOs = processedMabdMatrix?.splitPOs?.[activeGroupView] || [];

                if (splitPOs.length === 0) {
                  return (
                    <div className="p-4 text-center text-gray-400 text-xs">
                      No split POs for {activeGroupView}
                    </div>
                  );
                }

                return (
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-600">PO</th>
                        <th className="px-2 py-1.5 text-center font-semibold text-gray-600">Loads</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-600">First Appt</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Last Appt</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-600">MABD</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {splitPOs.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 text-gray-900 font-medium">{row.po_number}</td>
                          <td className="px-2 py-1.5 text-center text-gray-600">{row.load_count}</td>
                          <td className="px-2 py-1.5 text-gray-600">{row.first_appt_date || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-600">{row.last_appt_date || '—'}</td>
                          <td className="px-2 py-1.5 text-gray-600">{row.mabd || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Row 3: Table 4 | Table 5 | Table 6 (each 1/3) */}
        <div className="grid grid-cols-3 gap-4">
          {/* Table 4: Open Orders by MABD */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-3 py-2 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700">Open Confirmed by MABD</span>
                <span className="text-xs text-gray-400">|</span>
                <div className="flex gap-1">
                  {groupOrder.map(groupName => {
                    const openGroup = processedMabdMatrix?.openOrdersGroups?.[groupName];
                    const styles = groupStyles[groupName];
                    const isActive = activeGroupView === groupName;
                    const total = openGroup?.summary?.grandTotal || 0;

                    return (
                      <button
                        key={groupName}
                        onClick={() => setActiveGroupView(groupName)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                          isActive
                            ? `${styles.activeBg} ${styles.activeText}`
                            : `${styles.inactiveBg} ${styles.inactiveText} ${styles.hoverBg}`
                        }`}
                      >
                        {groupName}
                        {total > 0 && (
                          <span className={`ml-1 ${isActive ? 'opacity-80' : 'opacity-60'}`}>
                            {formatCurrency(total)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {renderOpenOrdersTable(processedMabdMatrix?.openOrdersGroups?.[activeGroupView], table4SortDir, () => setTable4SortDir(d => d === 'asc' ? 'desc' : 'asc'))}
          </div>

          {/* Table 5: Volume by Day Bar Chart */}
          <div className="bg-white rounded-lg shadow">
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Volume by Day</span>
                {(selectedCell || selectedPendingCell) && (selectedCell?.mabd || selectedPendingCell?.mabd) !== '_ALL_' && (
                  <span className="text-xs text-blue-600">
                    MABD: {selectedCell?.mabd || selectedPendingCell?.mabd}
                  </span>
                )}
              </div>
              <div className="p-3 overflow-x-auto">
                {(() => {
                  const volumeData = processedMabdMatrix?.volumeByDayData || [];
                  const warehouses = processedMabdMatrix?.warehouses || [];

                  if (volumeData.length === 0) {
                    return <div className="text-center text-gray-400 text-xs py-4">No volume data</div>;
                  }

                  // Warehouse colors
                  const warehouseColors = {
                    'LAP': 'bg-blue-500',
                    'BL': 'bg-green-500',
                    'CLS': 'bg-purple-500',
                    'MG': 'bg-orange-500',
                    'BWI': 'bg-red-500',
                    'DSI': 'bg-teal-500',
                    'DSD': 'bg-pink-500'
                  };
                  const defaultColors = ['bg-cyan-500', 'bg-amber-500', 'bg-lime-500', 'bg-indigo-500', 'bg-rose-500'];
                  const getWarehouseColor = (wh, idx) => warehouseColors[wh] || defaultColors[idx % defaultColors.length];

                  // Filter data by selected date if any
                  const selectedDate = selectedCell?.mabd || selectedPendingCell?.mabd;
                  let filteredData = volumeData;
                  if (selectedDate && selectedDate !== '_ALL_') {
                    filteredData = volumeData.filter(d => d.date === selectedDate);
                  }

                  if (filteredData.length === 0) {
                    return <div className="text-center text-gray-400 text-xs py-4">No data</div>;
                  }

                  // Calculate max total for scaling
                  const maxValue = Math.max(...filteredData.map(d => {
                    return warehouses.reduce((sum, wh) => sum + (d[wh] || 0), 0);
                  }));

                  return (
                    <div className="space-y-2">
                      {/* Legend */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        {warehouses.map((wh, idx) => (
                          <div key={wh} className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded ${getWarehouseColor(wh, idx)}`}></div>
                            <span className="text-xs text-gray-600">{wh}</span>
                          </div>
                        ))}
                      </div>
                      {/* Bars */}
                      <div className="space-y-1 max-h-[140px] overflow-y-auto">
                        {filteredData.map((day, idx) => {
                          const total = warehouses.reduce((sum, wh) => sum + (day[wh] || 0), 0);
                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 w-10 flex-shrink-0">{day.date}</span>
                              <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden flex">
                                {warehouses.map((wh, whIdx) => {
                                  const amt = day[wh] || 0;
                                  const pct = maxValue > 0 ? (amt / maxValue) * 100 : 0;
                                  if (pct === 0) return null;
                                  return (
                                    <div
                                      key={wh}
                                      className={`${getWarehouseColor(wh, whIdx)} h-full transition-all`}
                                      style={{ width: `${pct}%` }}
                                      title={`${wh}: ${formatCurrency(amt)}`}
                                    ></div>
                                  );
                                })}
                              </div>
                              <span className="text-xs text-gray-700 w-14 text-right flex-shrink-0">{formatCurrency(total)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

          {/* Table 6: Style Details for selection */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">Style Details</span>
              {(selectedPO || selectedCell || selectedPendingCell || selectedOpenMABD) && (
                <span className="text-xs text-blue-600">
                  {selectedPO ? `PO: ${selectedPO}` :
                   selectedOpenMABD ? `Open MABD: ${selectedOpenMABD}` :
                   selectedCell ? `MABD: ${selectedCell.mabd === '_ALL_' ? 'All' : selectedCell.mabd}` :
                   `MABD: ${selectedPendingCell?.mabd === '_ALL_' ? 'All' : selectedPendingCell?.mabd}`}
                </span>
              )}
            </div>
            <div className="overflow-auto max-h-[180px]">
              {(() => {
                // Get filtered POs based on current selection
                const details = processedMabdMatrix?.poDetails || [];
                let filteredPOs = [];
                let isOpenOrders = false;  // Track if we're looking at Open orders

                if (selectedPO) {
                  // Single PO selected
                  filteredPOs = [selectedPO];
                } else if (selectedOpenMABD) {
                  // Table 4 (Open Orders) selection
                  isOpenOrders = true;
                  const key = `${activeGroupView}|${selectedOpenMABD}`;
                  filteredPOs = processedMabdMatrix?.openOrderPOsByMABD?.[key] || [];
                } else if (selectedCell || selectedPendingCell) {
                  // Filter based on Table 1 or Table 2 selection
                  let filtered;
                  if (selectedCell) {
                    filtered = details.filter(d => {
                      if (d.group_name !== activeGroupView) return false;
                      const mabdMatch = selectedCell.mabd === '_ALL_' || d.mabd === selectedCell.mabd;
                      if (!mabdMatch) return false;
                      if (selectedCell.appointmentDate === '_ALL_') return true;
                      if (selectedCell.appointmentDate === 'Not Routed') return d.appointment_date === 'NOT_ROUTED';
                      if (selectedCell.appointmentDate === 'Pending') return !d.appointment_date || d.appointment_date === '';
                      return d.appointment_date === selectedCell.appointmentDate;
                    });
                  } else {
                    filtered = details.filter(d => {
                      if (d.group_name !== activeGroupView) return false;
                      if (d.appointment_date && d.appointment_date !== '' && d.appointment_date !== 'NOT_ROUTED') return false;
                      if (d.appointment_date === 'NOT_ROUTED') return false;
                      const mabdMatch = selectedPendingCell.mabd === '_ALL_' || d.mabd === selectedPendingCell.mabd;
                      return mabdMatch;
                    });
                  }
                  filteredPOs = filtered.map(d => d.po_no);
                }

                if (filteredPOs.length === 0) {
                  return (
                    <div className="p-4 text-center text-gray-400 text-xs">
                      Click a cell in Table 1, 2, 4, or a PO in Table 3
                    </div>
                  );
                }

                // Aggregate style details for all filtered POs
                // Use openStyleDetails for Open orders, styleDetails for Pick orders
                const styleSource = isOpenOrders ? processedMabdMatrix?.openStyleDetails : processedMabdMatrix?.styleDetails;
                const styleMap = {};
                filteredPOs.forEach(po => {
                  const styles = styleSource?.[po] || [];
                  styles.forEach(s => {
                    const key = `${s.style}|${s.style_name || ''}`;
                    if (!styleMap[key]) {
                      styleMap[key] = { style: s.style, style_name: s.style_name, qty: 0, nsr: 0 };
                    }
                    styleMap[key].qty += s.qty || 0;
                    styleMap[key].nsr += s.nsr || 0;
                  });
                });

                const aggregatedStyles = Object.values(styleMap).sort((a, b) => b.nsr - a.nsr);

                if (aggregatedStyles.length === 0) {
                  return (
                    <div className="p-4 text-center text-gray-400 text-xs">
                      No style details for selection ({filteredPOs.length} POs)
                    </div>
                  );
                }

                return (
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Style</th>
                        <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Style Name</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Qty</th>
                        <th className="px-2 py-1.5 text-right font-semibold text-gray-600">NSR</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {aggregatedStyles.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 text-gray-900 font-medium">{row.style}</td>
                          <td className="px-2 py-1.5 text-gray-600">{row.style_name || '—'}</td>
                          <td className="px-2 py-1.5 text-right text-gray-900">{row.qty?.toLocaleString() || '—'}</td>
                          <td className="px-2 py-1.5 text-right text-gray-900">{formatCurrency(row.nsr || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Routing Notes tab
  const renderNotesTab = () => {
    const reasonOptions = [
      { value: '', label: 'Select reason...' },
      { value: 'no_show', label: 'No Show' },
      { value: 'not_ready', label: 'Not Ready' },
      { value: 'other', label: 'Other' }
    ];

    const responsibilityOptions = [
      { value: '', label: 'Select responsibility...' },
      { value: 'warehouse', label: 'Warehouse' },
      { value: 'driver', label: 'Driver' },
      { value: 'inmocean', label: 'InMocean' },
      { value: 'bwi', label: 'BWI' },
      { value: 'imga', label: 'IMGA' }
    ];

    const formatNoteDate = (isoString) => {
      if (!isoString) return '—';
      const date = new Date(isoString);
      return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    };

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Routing Notes</h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                {routingNotes.length} notes
              </span>
            </div>
            <button
              onClick={() => loadRoutingNotes()}
              disabled={notesLoading}
              className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${notesLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Search Order:</span>
            <select
              value={notesSearch.type}
              onChange={(e) => { setNotesSearch(prev => ({ ...prev, type: e.target.value })); setSearchedOrder(null); }}
              className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="po">PO</option>
              <option value="pick_ticket">Pick Ticket</option>
              <option value="load">Load ID</option>
            </select>
            <input
              type="text"
              value={notesSearch.value}
              onChange={(e) => setNotesSearch(prev => ({ ...prev, value: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleNotesSearch()}
              placeholder={`Enter ${notesSearch.type === 'po' ? 'PO number' : notesSearch.type === 'pick_ticket' ? 'Pick Ticket' : 'Load ID'}...`}
              className="text-sm border border-gray-300 rounded px-3 py-1.5 w-48"
            />
            <button
              onClick={handleNotesSearch}
              disabled={notesSearching}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {notesSearching ? 'Searching...' : 'Search'}
            </button>
            {(notesSearch.value || searchedOrder) && (
              <button
                onClick={() => { setNotesSearch({ type: 'po', value: '' }); setSearchedOrder(null); resetNoteForm(); }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Order Details (shown after search) */}
        {searchedOrder && (
          <div className="bg-blue-50 rounded-lg shadow px-4 py-3 border border-blue-200">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-2">Order Details</h3>
                <div className="grid grid-cols-4 gap-x-6 gap-y-1 text-xs">
                  <div><span className="text-gray-500">PO:</span> <span className="font-medium text-gray-900">{searchedOrder.po_number || '—'}</span></div>
                  <div><span className="text-gray-500">Pick Ticket:</span> <span className="font-medium text-gray-900">{searchedOrder.pick_ticket || '—'}</span></div>
                  <div><span className="text-gray-500">Load ID:</span> <span className="font-medium text-gray-900">{searchedOrder.load_id || '—'}</span></div>
                  <div><span className="text-gray-500">Customer:</span> <span className="font-medium text-gray-900">{searchedOrder.customer_name || '—'}</span></div>
                  <div><span className="text-gray-500">Warehouse:</span> <span className="font-medium text-gray-900">{searchedOrder.warehouse || '—'}</span></div>
                  <div><span className="text-gray-500">MABD:</span> <span className="font-medium text-gray-900">{searchedOrder.mabd || '—'}</span></div>
                  <div><span className="text-gray-500">TMS Appt:</span> <span className="font-medium text-blue-600">{searchedOrder.appointment_date || 'Not scheduled'}</span></div>
                  <div><span className="text-gray-500">Mode:</span> <span className="font-medium text-gray-900">{searchedOrder.mode || '—'}</span></div>
                  <div><span className="text-gray-500">Amount:</span> <span className="font-medium text-gray-900">${searchedOrder.total_amount?.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0}) || '0'}</span></div>
                  <div><span className="text-gray-500">Status:</span> <span className="font-medium text-gray-900">{searchedOrder.status === 'P' ? 'Pick' : searchedOrder.status === 'O' ? 'Open' : searchedOrder.status || '—'}</span></div>
                  {searchedOrder.po_count > 1 && (
                    <div className="col-span-2"><span className="text-gray-500">POs on Load:</span> <span className="font-medium text-orange-600">{searchedOrder.po_count} POs</span></div>
                  )}
                </div>
              </div>
              {editingNote && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Existing Note Found</span>
              )}
            </div>
          </div>
        )}

        {/* Add/Edit Note Form */}
        <div className="bg-white rounded-lg shadow px-4 py-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {editingNote ? 'Edit Note' : 'Add New Note'}
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {/* Row 1: Type, Value, MABD */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={noteForm.lookup_type}
                onChange={(e) => setNoteForm(prev => ({ ...prev, lookup_type: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
                disabled={!!editingNote}
              >
                <option value="po">PO</option>
                <option value="pick_ticket">Pick Ticket</option>
                <option value="load">Load ID</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {noteForm.lookup_type === 'po' ? 'PO Number' : noteForm.lookup_type === 'pick_ticket' ? 'Pick Ticket' : 'Load ID'}
              </label>
              <input
                type="text"
                value={noteForm.lookup_value}
                onChange={(e) => setNoteForm(prev => ({ ...prev, lookup_value: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                disabled={!!editingNote}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">MABD</label>
              <input
                type="text"
                value={noteForm.mabd}
                onChange={(e) => setNoteForm(prev => ({ ...prev, mabd: e.target.value }))}
                placeholder="MM/DD"
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
              />
            </div>
            <div className="row-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
              <textarea
                value={noteForm.note}
                onChange={(e) => setNoteForm(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Enter note details..."
                rows={3}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 resize-none"
              />
            </div>

            {/* Row 2: Original Appt, Override Appt, Reason, Responsibility */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Original Appt Date</label>
              <input
                type="text"
                value={noteForm.original_appt_date}
                onChange={(e) => setNoteForm(prev => ({ ...prev, original_appt_date: e.target.value }))}
                placeholder="MM/DD/YYYY"
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Override Appt Date</label>
              <input
                type="text"
                value={noteForm.override_appt_date}
                onChange={(e) => setNoteForm(prev => ({ ...prev, override_appt_date: e.target.value }))}
                placeholder="MM/DD/YYYY"
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
              <select
                value={noteForm.reason}
                onChange={(e) => setNoteForm(prev => ({ ...prev, reason: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
              >
                {reasonOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Row 3: Responsibility and buttons */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Responsibility</label>
              <select
                value={noteForm.responsibility}
                onChange={(e) => setNoteForm(prev => ({ ...prev, responsibility: e.target.value }))}
                className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
              >
                {responsibilityOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-3 flex items-end justify-end gap-2">
              {editingNote && (
                <button
                  onClick={resetNoteForm}
                  className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={saveRoutingNote}
                disabled={notesSaving || !noteForm.lookup_value}
                className="px-6 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {notesSaving ? 'Saving...' : editingNote ? 'Update Note' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>

        {/* Recent Notes List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              {notesSearch.value ? `Search Results for "${notesSearch.value}"` : 'Recent Notes'}
            </h3>
          </div>
          <div className="overflow-auto max-h-[400px]">
            {notesLoading ? (
              <div className="p-8 text-center text-gray-400">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading notes...
              </div>
            ) : routingNotes.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                No notes found
              </div>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Type</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">PO/PT/Load</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">MABD</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Orig Appt</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Override</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Reason</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Resp.</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Note</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Updated</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {routingNotes.map((note) => (
                    <tr key={note.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600 capitalize">{note.lookup_type?.replace('_', ' ')}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium">{note.lookup_value}</td>
                      <td className="px-3 py-2 text-gray-600">{note.mabd || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{note.original_appt_date || '—'}</td>
                      <td className="px-3 py-2 text-blue-600 font-medium">{note.override_appt_date || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 capitalize">{note.reason?.replace('_', ' ') || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 capitalize">{note.responsibility || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate" title={note.note}>{note.note || '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{formatNoteDate(note.updated_at)}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => editNote(note)}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  // CHA-463 — Target Item Dims: what we can send Target, and what is still missing.
  //
  // The missing list is the point of this screen, not an error state. Coverage is expected to
  // start low and climb: the Matson feed only knows what has already SHIPPED, and SP27 is
  // barely underway, so most items are simply waiting rather than broken. Splitting the
  // reasons is what makes that readable — 'not_in_dim_library' resolves itself week by week,
  // while 'no_upc_mapping' never will and needs a human.
  const DIM_MISSING_REASONS = {
    not_in_dim_library: {
      label: 'Not shipped yet',
      hint: 'Matson only sees shipped cartons. These resolve on their own as the season ships.',
      tone: 'text-slate-600 bg-slate-50 border-slate-200'
    },
    no_upc_mapping: {
      label: 'No UPC mapping',
      hint: 'No route from the style to a Target UPC — dims cannot be sent until the Target mapping exists.',
      tone: 'text-amber-700 bg-amber-50 border-amber-200'
    },
    mapping_found_no_upcs: {
      label: 'Mapped, no UPCs',
      hint: "The assortment exploded, but Target's item feed has no UPC for those components.",
      tone: 'text-amber-700 bg-amber-50 border-amber-200'
    },
    assortment_component_upc_not_uploadable: {
      label: 'Assortment UPC held',
      hint: 'Target gives component UPCs for this assortment. Upload held because a component UPC cannot be sent as the assortment item.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    },
    duplicate_upc_across_styles: {
      label: 'Duplicate UPC held',
      hint: 'The same UPC would be exported for more than one style. Upload held until the correct Target identifier is confirmed.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    },
    dims_unresolved: {
      label: 'Shipped, spec unclear',
      hint: 'Shipped, but no carton configuration was dominant enough to trust. Deliberately not guessed.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    },
    mp_qty_missing: {
      label: 'Missing MP qty',
      hint: 'Open WIP marks this size as MP, but the master-pack quantity is blank or zero. Upload held until MP_qty is fixed.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    },
    mp_qty_conflict: {
      label: 'Conflicting MP qty',
      hint: 'Open WIP has more than one non-zero MP_qty for the same style/colour/size. Upload held until the pack quantity is made consistent.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    },
    mp_carton_dims_missing: {
      label: 'Missing MP carton dims',
      hint: 'This MP row has a pack quantity, but Matson is missing carton dimensions or carton quantity. Upload held rather than falling back to each dims.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    },
    mp_manual_dims_need_pack_measurement: {
      label: 'MP needs pack measurement',
      hint: 'Manual dims are each-level measurements. MP rows need Matson carton data or a pack-level measurement before upload.',
      tone: 'text-rose-700 bg-rose-50 border-rose-200'
    }
  };

  // CHA-466 — download helpers. The CSV text itself is built by the pure module so it can be
  // tested; only the browser plumbing lives here.
  const downloadCsv = (text, filename) => {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const tenantArg = (extra = {}) => {
    const role = userProfile?.role;
    if (role === 'platform_owner' || role === 'platform_admin') {
      if (!tenantId) {
        throw new Error('Tenant context is required for platform users.');
      }
      return { tenantId, ...extra };
    }
    return extra;
  };

  const startManualDim = (m) => {
    if (manualDimSaving) return;
    setManualDimDraft({
      style: m.sty || '',
      sizeCode: m.size_code || '',
      upc: m.upc || '',
      h: '',
      w: '',
      d: '',
      weight: '',
      note: '',
    });
  };

  const saveManualDim = async () => {
    if (!manualDimDraft) return;
    setManualDimSaving(true);
    setError(null);
    try {
      const functions = getFunctions();
      const saveTargetManualDim = httpsCallable(functions, 'saveTargetManualDim');
      await saveTargetManualDim(tenantArg(manualDimDraft));
      const refreshed = await loadDimsData();
      if (refreshed) {
        setManualDimDraft(null);
        setDimsView('ready');
      }
    } catch (err) {
      console.error('Error saving manual dim:', err);
      setError(err.message);
    } finally {
      setManualDimSaving(false);
    }
  };

  const renderTargetDimsTab = () => {
    if (dimsLoading && !dimsData) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-red-600 animate-spin" />
          <span className="ml-2 text-gray-600">Loading Target dim coverage...</span>
        </div>
      );
    }

    if (!dimsData) {
      return (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Target className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No dim coverage loaded</p>
          <button
            onClick={loadDimsData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Load Coverage
          </button>
        </div>
      );
    }

    // `summary` is defaulted like the arrays (CodeRabbit review): it is read unguarded five
    // times below, and the deployed callable is not guaranteed to match this repo's shape —
    // the live functions currently run a revision older than these fields. A missing summary
    // would throw a TypeError and unmount the whole Logistics page, not just this tab.
    const { summary = {}, upload = [], missing = [], season, customer } = dimsData;
    const pct = summary.orderSkus ? Math.round((summary.skusCovered / summary.orderSkus) * 100) : 0;
    // Dim fields are nullable in BigQuery, and Number(undefined).toFixed() renders the string
    // "NaN" — in a table the operator is about to copy into Target. Every other table in this
    // file falls back to an em dash; this does too.
    const dimCell = (v, dp) => (Number.isFinite(Number(v)) && v !== null ? Number(v).toFixed(dp) : '—');

    return (
      <div className="space-y-4">
        {/* Coverage header */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">
                Item dims — {season} on order to {customer}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Derived from the weekly Matson carton export. Upload this week&apos;s file under
                Data &rarr; Target &rarr; Upload Files &rarr; Matson Carton Dims, then rebuild.
              </p>
            </div>
            <button
              onClick={rebuildDimLibrary}
              disabled={dimsRebuilding}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${dimsRebuilding ? 'animate-spin' : ''}`} />
              {dimsRebuilding ? 'Rebuilding...' : 'Rebuild from Matson'}
            </button>
          </div>

          {dimsRebuildResult && (
            <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Rebuilt from {dimsRebuildResult.rawRows?.toLocaleString()} carton rows —{' '}
              {dimsRebuildResult.resolved} items resolved, {dimsRebuildResult.unresolved} held back.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="text-2xl font-semibold text-slate-800">{summary.orderSkus}</div>
              <div className="text-xs text-slate-500 mt-0.5">SKUs on order</div>
            </div>
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
              <div className="text-2xl font-semibold text-emerald-700">{summary.skusCovered}</div>
              <div className="text-xs text-emerald-600 mt-0.5">ready to send ({pct}%)</div>
            </div>
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
              <div className="text-2xl font-semibold text-amber-700">{summary.skusMissing}</div>
              <div className="text-xs text-amber-600 mt-0.5">missing dims</div>
            </div>
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="text-2xl font-semibold text-slate-800">{upload.length}</div>
              <div className="text-xs text-slate-500 mt-0.5">upload rows (one per UPC)</div>
            </div>
          </div>

          {/* Partial coverage is called out separately: a SKU with SOME of its UPCs resolved is
              NOT ready to send, and hiding it inside "covered" is how a half-finished upload
              looks finished. */}
          {summary.skusPartiallyCovered > 0 && (
            <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {summary.skusPartiallyCovered} SKU(s) have dims for only some of their UPCs — counted as
              missing, not ready.
            </div>
          )}

          {manualDimDraft && (
            <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-amber-900">
                    Manual dims for {manualDimDraft.style}{manualDimDraft.sizeCode ? ` / ${manualDimDraft.sizeCode}` : ''}
                    {manualDimDraft.upc ? ` / UPC ${manualDimDraft.upc}` : ''}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Manual values fill missing UPCs; UPC-specific values override Matson for that UPC.
                  </p>
                </div>
                <button
                  onClick={() => setManualDimDraft(null)}
                  disabled={manualDimSaving}
                  className="text-sm text-amber-700 hover:text-amber-900"
                >
                  Cancel
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                {[
                  ['h', 'H'],
                  ['w', 'W'],
                  ['d', 'D'],
                  ['weight', 'Weight'],
                ].map(([key, label]) => (
                  <label key={key} className="text-xs text-amber-900">
                    {label}
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={manualDimDraft[key]}
                      onChange={(e) => setManualDimDraft(v => ({ ...v, [key]: e.target.value }))}
                      disabled={manualDimSaving}
                      className="mt-1 w-full rounded border border-amber-200 bg-white px-2 py-1 text-sm text-slate-800"
                    />
                  </label>
                ))}
                <label className="text-xs text-amber-900">
                  Note
                  <input
                    value={manualDimDraft.note}
                    onChange={(e) => setManualDimDraft(v => ({ ...v, note: e.target.value }))}
                    disabled={manualDimSaving}
                    className="mt-1 w-full rounded border border-amber-200 bg-white px-2 py-1 text-sm text-slate-800"
                  />
                </label>
              </div>
              <button
                onClick={saveManualDim}
                disabled={manualDimSaving}
                className="mt-3 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {manualDimSaving ? 'Saving...' : 'Save manual dims'}
              </button>
            </div>
          )}
        </div>

        {/* CHA-465 — the advisory. Sits ABOVE the tables on purpose: the page should say what
            needs doing, not make the operator infer it from a grid. */}
        {dimsData.drift === null || dimsData.drift === undefined ? (
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-slate-300">
            <p className="text-slate-700 font-medium">Target&apos;s current values aren&apos;t loaded</p>
            <p className="text-sm text-slate-500 mt-1">
              Upload a Target item export under Data &rarr; Target Item Export and this will show which
              pick orders still need sending. Without it we can only say what dims we <em>have</em>, not
              which ones Target is missing.
            </p>
          </div>
        ) : dimsData.drift.summary.needsUploadItems > 0 ? (
          <div className="bg-amber-50 rounded-lg shadow p-5 border-l-4 border-amber-400">
            <p className="text-amber-900 font-medium">
              {dimsData.drift.summary.needsUploadItems} Target item(s) on pick have dims Target doesn&apos;t have
            </p>
            <p className="text-sm text-amber-800 mt-1">
              {dimsData.drift.summary.needsUploadOnPlaceholder > 0 && (
                <>{dimsData.drift.summary.needsUploadOnPlaceholder} still on Target&apos;s 1×5×5 placeholder
                  (never had a real dim){dimsData.drift.summary.needsUploadOnOtherValue > 0 ? '; ' : '. '}</>
              )}
              {dimsData.drift.summary.needsUploadOnOtherValue > 0 && (
                <>{dimsData.drift.summary.needsUploadOnOtherValue} row(s) hold a different value — if you
                  already sent those, the upload didn&apos;t apply and is worth checking. </>
              )}
              Target snapshot as of {dimsData.drift.asOf || 'unknown'} ({dimsData.drift.snapshotItems} items).
            </p>
            <div className="mt-3 overflow-x-auto max-h-64 overflow-y-auto bg-white rounded border border-amber-200">
              <table className="min-w-full text-sm">
                <thead className="bg-amber-100/60 sticky top-0">
                  <tr className="text-left text-amber-900">
                    <th className="px-3 py-2 font-medium">TCIN</th>
                    <th className="px-3 py-2 font-medium">Style</th>
                    <th className="px-3 py-2 font-medium">Target has</th>
                    <th className="px-3 py-2 font-medium">Send</th>
                  </tr>
                </thead>
                <tbody>
                  {dimsData.drift.needsUpload.map((r, i) => (
                    <tr key={`${r.upc}-${i}`} className="border-t border-amber-100">
                      <td className="px-3 py-2 font-mono text-xs">{r.tcin || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.sty}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {r.targetDims
                          ? `${dimCell(r.targetDims.h, 2)} / ${dimCell(r.targetDims.w, 2)} / ${dimCell(r.targetDims.d, 2)} @ ${dimCell(r.targetDims.weight, 3)}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {`${dimCell(r.h_in, 2)} / ${dimCell(r.w_in, 2)} / ${dimCell(r.d_in, 2)} @ ${dimCell(r.weight_lb, 3)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : dimsData.drift.summary.notInTarget > 0 ? (
          // Codex review, PR #353 — do NOT show the green state while items are absent from
          // Target's export. needsUploadItems can be zero while notInTarget is positive: those
          // items exist on pick with dims ready, but Target has no record of them at all, so
          // they need the SETUP path rather than a dim update. Calling that "up to date" would
          // hide real work behind a reassuring banner.
          <div className="bg-sky-50 rounded-lg shadow p-5 border-l-4 border-sky-400">
            <p className="text-sky-900 font-medium">
              Everything Target knows about is up to date — but {dimsData.drift.summary.notInTarget} row(s)
              aren&apos;t in its export
            </p>
            <p className="text-sm text-sky-800 mt-1">
              Those items have dims ready and no matching record in the snapshot, so they likely need
              Setup (or belong to an item type whose export hasn&apos;t been uploaded). Snapshot as of{' '}
              {dimsData.drift.asOf || 'unknown'} ({dimsData.drift.snapshotItems} items).
            </p>
          </div>
        ) : (
          <div className="bg-emerald-50 rounded-lg shadow p-5 border-l-4 border-emerald-400">
            <p className="text-emerald-900 font-medium">Everything we have dims for is up to date at Target</p>
            <p className="text-sm text-emerald-800 mt-1">
              Snapshot as of {dimsData.drift.asOf || 'unknown'} ({dimsData.drift.snapshotItems} items).
              Items still missing dims are listed below — those are waiting on Matson, not on an upload.
            </p>
          </div>
        )}

        {/* CHA-467 — items whose carton spec keeps moving between Matson weeks.
            Silent until a second week is loaded: one week gives one configuration per UPC, so
            nothing can disagree yet. That is expected on day one, not a missing feature. */}
        {(dimsData.unstable || []).length > 0 && (
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-orange-400">
            <p className="text-orange-900 font-medium">
              {dimsData.unstable.length} item(s) about to be sent have a carton spec that keeps changing
            </p>
            <p className="text-sm text-orange-800 mt-1">
              These shipped in more than one carton configuration. The most-shipped one is being used —
              worth asking the warehouse to confirm before it becomes a number at Target.
            </p>
            <div className="mt-3 space-y-3">
              {(dimsUnstableAll ? dimsData.unstable : dimsData.unstable.slice(0, 10)).map((u, i) => (
                <div key={`${u.upc}-${i}`} className="border border-orange-200 rounded-lg p-3 bg-orange-50/40">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className="font-mono text-sm text-slate-800">
                      {u.style || u.upc}{u.size ? ` · ${u.size}` : ''}
                    </span>
                    <span className="text-xs text-orange-700">
                      {u.configsSeen} configurations across {u.cartonsObserved} cartons ·
                      {' '}using the one backed by {Math.round((u.configShare || 0) * 100)}%
                    </span>
                  </div>
                  {Array.isArray(u.configurations) && u.configurations.length > 0 && (
                    <table className="mt-2 text-xs w-full">
                      <tbody>
                        {u.configurations.map((c, j) => (
                          <tr key={j} className={c.isWinner ? 'font-medium text-slate-800' : 'text-slate-500'}>
                            <td className="py-0.5 pr-3">
                              {c.isWinner ? '→ using' : '   also seen'}
                            </td>
                            <td className="py-0.5 pr-3 font-mono">
                              {c.cartonDimsCm
                                ? `${c.cartonDimsCm.l} × ${c.cartonDimsCm.w} × ${c.cartonDimsCm.h} cm`
                                : '—'} @ {c.qtyPerCarton}
                            </td>
                            <td className="py-0.5 text-right">
                              {c.cartons} cartons ({Math.round((c.share || 0) * 100)}%)
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
              {dimsData.unstable.length > 10 && (
                <button
                  type="button"
                  onClick={() => setDimsUnstableAll(v => !v)}
                  className="text-xs text-orange-700 underline hover:text-orange-900"
                >
                  {dimsUnstableAll
                    ? 'Show fewer'
                    : `Show all ${dimsData.unstable.length} unstable items`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Reason breakdown */}
        <div className="bg-white rounded-lg shadow p-5">
          <h4 className="font-medium text-slate-800 mb-3">Why dims are missing</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(summary.missingByReason || {}).map(([reason, count]) => {
              const meta = DIM_MISSING_REASONS[reason] || {
                label: reason, hint: '', tone: 'text-slate-600 bg-slate-50 border-slate-200'
              };
              return (
                <div key={reason} className={`border rounded-lg p-3 ${meta.tone}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">{meta.label}</span>
                    <span className="text-lg font-semibold">{count}</span>
                  </div>
                  {meta.hint && <p className="text-xs mt-1 opacity-80">{meta.hint}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail table */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex border-b border-slate-100">
            <button
              onClick={() => setDimsView('missing')}
              className={`px-4 py-2 text-sm font-medium ${
                dimsView === 'missing'
                  ? 'text-[#cc0000] border-b-2 border-[#cc0000] -mb-px'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Missing ({missing.length})
            </button>
            <button
              onClick={() => setDimsView('ready')}
              className={`px-4 py-2 text-sm font-medium ${
                dimsView === 'ready'
                  ? 'text-[#cc0000] border-b-2 border-[#cc0000] -mb-px'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Ready to send ({upload.length})
            </button>
            {/* CHA-466 — download whichever list is showing. Disabled rather than hidden when
                empty, so the control does not appear and vanish as the data changes. */}
            <div className="ml-auto flex items-center pr-3">
              <button
                onClick={() => (dimsView === 'ready'
                  ? downloadCsv(buildDimsCsv(upload), timestampedName('target-dims-ready'))
                  : downloadCsv(buildMissingCsv(missing, DIM_MISSING_REASONS), timestampedName('target-dims-missing')))}
                disabled={dimsView === 'ready' ? upload.length === 0 : missing.length === 0}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                title={dimsView === 'ready'
                  ? 'Download the values to send to Target'
                  : 'Download the missing-dims worklist'}
              >
                <Download className="h-4 w-4" />
                Download CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            {dimsView === 'missing' ? (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-medium">Style</th>
                    <th className="px-3 py-2 font-medium">Colour</th>
                    <th className="px-3 py-2 font-medium">Size</th>
                    <th className="px-3 py-2 font-medium">Stage</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                    <th className="px-3 py-2 font-medium text-right">UPCs</th>
                    <th className="px-3 py-2 font-medium text-right">Manual</th>
                  </tr>
                </thead>
                <tbody>
                  {missing.map((m, i) => (
                    <tr key={`${m.sty}-${i}`} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{m.sty}</td>
                      <td className="px-3 py-2">{m.clr || '—'}</td>
                      <td className="px-3 py-2">{m.size_code || '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{m.stage || '—'}</td>
                      <td className="px-3 py-2">
                        {(DIM_MISSING_REASONS[m.reason] || {}).label || m.reason}
                        {m.partial && <span className="ml-2 text-xs text-amber-700">(partial)</span>}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-500">{m.upcsChecked ?? 0}</td>
                      <td className="px-3 py-2 text-right">
                        {targetDimsCanEditManual && ['not_in_dim_library', 'dims_unresolved'].includes(m.reason) ? (
                          <button
                            onClick={() => startManualDim(m)}
                            disabled={manualDimSaving}
                            className="text-xs text-amber-700 hover:text-amber-900 underline"
                          >
                            Add
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-slate-600">
                    <th className="px-3 py-2 font-medium">Style</th>
                    <th className="px-3 py-2 font-medium">UPC</th>
                    <th className="px-3 py-2 font-medium">Via</th>
                    <th className="px-3 py-2 font-medium text-right">H (in)</th>
                    <th className="px-3 py-2 font-medium text-right">W (in)</th>
                    <th className="px-3 py-2 font-medium text-right">D (in)</th>
                    <th className="px-3 py-2 font-medium text-right">Weight (lb)</th>
                    <th className="px-3 py-2 font-medium text-right">Pack Qty</th>
                    <th className="px-3 py-2 font-medium">Unit</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {upload.map((u, i) => (
                    <tr key={`${u.upc}-${i}`} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">{u.sty}</td>
                      <td className="px-3 py-2 font-mono text-xs">{u.upc}</td>
                      <td className="px-3 py-2 text-slate-500">{u.route}</td>
                      <td className="px-3 py-2 text-right">{dimCell(u.h_in, 2)}</td>
                      <td className="px-3 py-2 text-right">{dimCell(u.w_in, 2)}</td>
                      <td className="px-3 py-2 text-right">{dimCell(u.d_in, 2)}</td>
                      <td className="px-3 py-2 text-right">{dimCell(u.weight_lb, 3)}</td>
                      <td className="px-3 py-2 text-right">{u.packQty ?? '—'}</td>
                      <td className="px-3 py-2">{u.unit || 'Each'}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {u.source || 'matson'}
                        {u.derivedDims && <span className="block text-xs text-amber-700">Matson also present</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Weight is derived from GROSS carton weight, so it is a ceiling on the true item
              weight, not a measurement. Saying so here keeps anyone from reading it as exact. */}
          {dimsView === 'ready' && upload.length > 0 && (
            <div className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
              Weight is derived from gross carton weight and therefore an upper bound — the true item
              weighs less. H/W/D are modelled from the carton and units per carton; verify before
              uploading at scale.
            </div>
          )}
        </div>
      </div>
    );
  };

  // Target Routing Tab
  const renderTargetTab = () => {
    if (targetLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-red-600 animate-spin" />
          <span className="ml-2 text-gray-600">Loading Target routing data...</span>
        </div>
      );
    }

    if (!targetData) {
      return (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Target className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No Target routing data available</p>
          <button
            onClick={loadTargetData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Load Data
          </button>
        </div>
      );
    }

    const { matrix, statuses, poDetails, styleDetails, summary } = targetData;

    // Filter PO details based on selected cell
    const filteredPODetails = targetSelectedCell
      ? poDetails.filter(po => {
          const matchMABD = po.mabd === targetSelectedCell.mabd;
          const matchStatus = targetSelectedCell.status === 'all' || po.routing_status === targetSelectedCell.status;
          const matchInYard = targetSelectedCell.inYardDate === 'all' ||
            (po.in_yard_goal_date || 'No TMS Data') === targetSelectedCell.inYardDate;
          return matchMABD && (matchStatus || matchInYard);
        })
      : poDetails;

    // Get styles for selected PO
    const selectedStyles = targetSelectedPO ? (styleDetails[targetSelectedPO] || []) : [];

    // Format currency
    const formatCurrency = (val) => {
      if (!val && val !== 0) return '-';
      return '$' + Math.round(val).toLocaleString();
    };

    return (
      <div className="space-y-6">
        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase">Total Amount</p>
            <p className="text-xl font-semibold text-gray-900">{formatCurrency(summary?.totalAmount)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase">Total POs</p>
            <p className="text-xl font-semibold text-gray-900">{summary?.totalPOs || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase">Routed</p>
            <p className="text-xl font-semibold text-green-600">{summary?.routedCount || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase">Not Routed</p>
            <p className="text-xl font-semibold text-red-600">{summary?.notRoutedCount || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500 uppercase">Routed %</p>
            <p className="text-xl font-semibold text-blue-600">{summary?.routedPercent || 0}%</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Table 1: Status Matrix */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Pick Orders by MABD</h3>
              <button
                onClick={() => setTargetSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                MABD {targetSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
            </div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">MABD</th>
                    <th className="px-2 py-2 text-right font-medium text-gray-600">Not Routed</th>
                    <th className="px-2 py-2 text-right font-medium text-gray-600">Routed</th>
                    <th className="px-2 py-2 text-right font-medium text-gray-600">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[...matrix]
                    .sort((a, b) => {
                      if (!a.mabd_date) return 1;
                      if (!b.mabd_date) return -1;
                      const dateA = new Date(a.mabd_date?.value || a.mabd_date);
                      const dateB = new Date(b.mabd_date?.value || b.mabd_date);
                      return targetSortDir === 'asc' ? dateA - dateB : dateB - dateA;
                    })
                    .map((row, idx) => {
                      // Calculate totals for this MABD
                      let notRouted = 0;
                      let routed = 0;
                      Object.entries(row.byInYardDate || {}).forEach(([inYard, statusData]) => {
                        Object.entries(statusData).forEach(([status, data]) => {
                          if (status === 'NOT_ROUTED') {
                            notRouted += data.amount || 0;
                          } else {
                            routed += data.amount || 0;
                          }
                        });
                      });
                      const total = notRouted + routed;

                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-2 py-1.5 font-medium text-gray-700">{row.mabd || '-'}</td>
                          <td
                            className={`px-2 py-1.5 text-right cursor-pointer ${
                              targetSelectedCell?.mabd === row.mabd && targetSelectedCell?.status === 'NOT_ROUTED'
                                ? 'bg-red-100 text-red-700 font-medium'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                            onClick={() => setTargetSelectedCell({ mabd: row.mabd, status: 'NOT_ROUTED', inYardDate: 'all' })}
                          >
                            {notRouted > 0 ? formatCurrency(notRouted) : '-'}
                          </td>
                          <td
                            className={`px-2 py-1.5 text-right cursor-pointer ${
                              targetSelectedCell?.mabd === row.mabd && targetSelectedCell?.status !== 'NOT_ROUTED' && targetSelectedCell?.status !== 'all'
                                ? 'bg-green-100 text-green-700 font-medium'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            onClick={() => setTargetSelectedCell({ mabd: row.mabd, status: 'ROUTED', inYardDate: 'all' })}
                          >
                            {routed > 0 ? formatCurrency(routed) : '-'}
                          </td>
                          <td
                            className={`px-2 py-1.5 text-right cursor-pointer font-medium ${
                              targetSelectedCell?.mabd === row.mabd && targetSelectedCell?.status === 'all'
                                ? 'bg-blue-100 text-blue-700'
                                : 'text-gray-700 hover:bg-blue-50'
                            }`}
                            onClick={() => setTargetSelectedCell({ mabd: row.mabd, status: 'all', inYardDate: 'all' })}
                          >
                            {formatCurrency(total)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 2: PO Details */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                PO Details
                {targetSelectedCell && (
                  <span className="ml-2 text-xs text-gray-500">
                    (MABD: {targetSelectedCell.mabd}, {filteredPODetails.length} POs)
                  </span>
                )}
              </h3>
              {targetSelectedCell && (
                <button
                  onClick={() => setTargetSelectedCell(null)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear Filter
                </button>
              )}
            </div>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">PO</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">Store</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">Div</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">WH</th>
                    <th className="px-2 py-2 text-right font-medium text-gray-600">Amount</th>
                    <th className="px-2 py-2 text-right font-medium text-gray-600">Ctns</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">MABD</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">Status</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">Pickup</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">In Yard Goal</th>
                    <th className="px-2 py-2 text-left font-medium text-gray-600">Carrier</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPODetails.slice(0, 200).map((po, idx) => (
                    <tr
                      key={idx}
                      className={`hover:bg-gray-50 cursor-pointer ${
                        targetSelectedPO === po.po_no ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setTargetSelectedPO(po.po_no)}
                    >
                      <td className="px-2 py-1.5 font-medium text-blue-600">{po.po_no}</td>
                      <td className="px-2 py-1.5 text-gray-600">{po.store || '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{po.division || '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{po.warehouse || '-'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{formatCurrency(po.amount)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-600">{po.cartons || '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{po.mabd || '-'}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          po.routing_status === 'NOT_ROUTED'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {po.routing_status || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-600">{po.pickup_date || '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{po.in_yard_goal_date || '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{po.executing_scac || po.assigned_scac || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredPODetails.length > 200 && (
                <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50">
                  Showing 200 of {filteredPODetails.length} POs
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Style Details */}
        {targetSelectedPO && selectedStyles.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                Style Details for {targetSelectedPO}
              </h3>
              <button
                onClick={() => setTargetSelectedPO(null)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Close
              </button>
            </div>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Style</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Color</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Size</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Qty</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Amount</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">NSR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {selectedStyles.map((style, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-medium text-gray-700">{style.style}</td>
                      <td className="px-3 py-1.5 text-gray-600">{style.style_name || '-'}</td>
                      <td className="px-3 py-1.5 text-gray-600">{style.color || '-'}</td>
                      <td className="px-3 py-1.5 text-gray-600">{style.dimension || '-'}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700">{style.qty?.toLocaleString() || '-'}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700">{formatCurrency(style.amount)}</td>
                      <td className="px-3 py-1.5 text-gray-600">{style.nsr || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Placeholder for other tabs
  const renderPlaceholder = (title) => (
    <div className="bg-white rounded-lg shadow p-12 text-center">
      <div className="text-gray-400 mb-4">
        <Target className="h-16 w-16 mx-auto" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500">Coming soon...</p>
    </div>
  );

  // Render BL Log tab
  const renderBLLogTab = () => {
    const columns = [
      { key: 'routing_status', label: 'Routing Status', width: 'w-24', sortable: true },
      { key: 'customer_name', label: 'Customer', width: 'w-32', sortable: true },
      { key: 'log_division', label: 'Log Div', width: 'w-16', sortable: true },
      { key: 'rabi_division', label: 'Rabi Div', width: 'w-16', sortable: true },
      { key: 'recv_date', label: 'Recv Date', width: 'w-20', sortable: true },
      { key: 'ship_to', label: 'Ship To', width: 'w-24', sortable: true },
      { key: 'pick_ticket', label: 'Pick Ticket', width: 'w-24', sortable: true },
      { key: 'po', label: 'PO', width: 'w-28', sortable: true },
      { key: 'start_date', label: 'Start', width: 'w-20', sortable: true },
      { key: 'cancel_date', label: 'Cancel', width: 'w-20', sortable: true },
      { key: 'ctns', label: 'Ctns', width: 'w-14', sortable: true },
      { key: 'pcs', label: 'Pcs', width: 'w-14', sortable: true },
      { key: 'wave', label: 'Wave', width: 'w-16', sortable: true },
      { key: 'special_project', label: 'SP', width: 'w-12' },
      { key: 'csr_comments', label: 'CSR Comments', width: 'w-32' },
      { key: 'routing_notes', label: 'Routing Notes', width: 'w-32' },
      { key: 'loads', label: 'Loads', width: 'w-16' },
      { key: 'carrier', label: 'Carrier', width: 'w-20', sortable: true },
      { key: 'ship_time', label: 'Time', width: 'w-16' },
      { key: 'ship_date', label: 'Ship Date', width: 'w-20', sortable: true },
      { key: 'route_date', label: 'Route Date', width: 'w-20', sortable: true },
      { key: 'shipped', label: 'Shipped', width: 'w-16', sortable: true },
      { key: 'img_notes', label: 'IMG Notes', width: 'w-32' },
      { key: 'target_load_data', label: 'Target Load', width: 'w-24' },
      { key: 'target_pu_date', label: 'Target PU', width: 'w-20' }
    ];

    // Get unique values for filters
    const uniqueLogDivs = [...new Set((blLogData || []).map(r => r.log_division).filter(Boolean))].sort();
    const uniqueRabiDivs = [...new Set((blLogData || []).map(r => r.rabi_division).filter(Boolean))].sort();
    const uniqueShipTo = [...new Set((blLogData || []).map(r => r.ship_to).filter(Boolean))].sort();
    const uniqueStatuses = [...new Set((blLogData || []).map(r => r.routing_status).filter(Boolean))].sort();

    const handleSort = (key) => {
      setBlLogSort(prev => ({
        key,
        dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
      }));
    };

    return (
      <div className="space-y-4">
        {/* Header with sync controls */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900">BL Shipping Log</h2>
              {blLogLastSync && (
                <span className="text-xs text-gray-500">
                  Last sync: {new Date(blLogLastSync).toLocaleString()}
                </span>
              )}
              {blLogData && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {filteredBlLogData.length} / {blLogData.length} rows
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.open(ROUTING_SHEET_URL, '_blank')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Open Sheet
              </button>
              <button
                onClick={syncBLLog}
                disabled={blLogSyncing}
                className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${blLogSyncing ? 'animate-spin' : ''}`} />
                {blLogSyncing ? 'Syncing...' : 'Sync from Sheets'}
              </button>
              <button
                onClick={loadBLLogData}
                disabled={blLogLoading}
                className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${blLogLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs font-medium text-gray-500">Filters:</span>
            <select
              value={blLogFilters.routing_status}
              onChange={(e) => setBlLogFilters(prev => ({ ...prev, routing_status: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Routing Status</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={blLogFilters.log_division}
              onChange={(e) => setBlLogFilters(prev => ({ ...prev, log_division: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Log Div</option>
              {uniqueLogDivs.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={blLogFilters.rabi_division}
              onChange={(e) => setBlLogFilters(prev => ({ ...prev, rabi_division: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Rabi Div</option>
              {uniqueRabiDivs.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={blLogFilters.ship_to}
              onChange={(e) => setBlLogFilters(prev => ({ ...prev, ship_to: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Ship To</option>
              {uniqueShipTo.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={blLogFilters.shipped}
              onChange={(e) => setBlLogFilters(prev => ({ ...prev, shipped: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Shipped</option>
              <option value="Y">Shipped</option>
              <option value="N">Not Shipped</option>
            </select>
            {(blLogFilters.log_division || blLogFilters.rabi_division || blLogFilters.ship_to || blLogFilters.shipped || blLogFilters.routing_status) && (
              <button
                onClick={() => setBlLogFilters({ log_division: '', rabi_division: '', ship_to: '', shipped: '', routing_status: '' })}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Data table */}
        <div className="bg-white rounded-lg shadow">
          {blLogLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
              <span className="ml-2 text-gray-600">Loading BL Log...</span>
            </div>
          ) : !blLogData || blLogData.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-2" />
              <p>No BL Log data available</p>
              <button
                onClick={syncBLLog}
                className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              >
                Sync from Google Sheets
              </button>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full text-xs border-collapse" style={{ minWidth: '1800px' }}>
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b-2 border-gray-200">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        onClick={() => col.sortable && handleSort(col.key)}
                        className={`px-2 py-2 text-left font-semibold text-gray-600 ${col.width} whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          {col.sortable && blLogSort.key === col.key && (
                            blLogSort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredBlLogData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className={`px-2 py-1.5 text-gray-700 ${col.width} whitespace-nowrap overflow-hidden text-ellipsis`}
                          title={row[col.key] || ''}
                        >
                          {col.key === 'shipped' ? (
                            row[col.key] === 'Y' || row[col.key] === 'Yes' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : row[col.key] ? (
                              <span className="text-gray-400">{row[col.key]}</span>
                            ) : '—'
                          ) : (
                            row[col.key] || '—'
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render LAP Log tab
  const renderLAPLogTab = () => {
    const columns = [
      { key: 'log_division', label: 'Log Div', width: 'w-28', sortable: true },
      { key: 'rabi_division', label: 'Division', width: 'w-16', sortable: true },
      { key: 'customer_name', label: 'Customer', width: 'w-32', sortable: true },
      { key: 'rcvd_date', label: 'Rcvd Date', width: 'w-20', sortable: true },
      { key: 'ship_to', label: 'Ship To', width: 'w-24', sortable: true },
      { key: 'pick_ticket', label: 'Pick Ticket', width: 'w-24', sortable: true },
      { key: 'po', label: 'PO', width: 'w-28', sortable: true },
      { key: 'cancel_date', label: 'Cancel', width: 'w-20', sortable: true },
      { key: 'start_date', label: 'Start', width: 'w-20', sortable: true },
      { key: 'ctns', label: 'Ctns', width: 'w-14', sortable: true },
      { key: 'pcs', label: 'Pcs', width: 'w-14', sortable: true },
      { key: 'wave', label: 'Wave', width: 'w-16', sortable: true },
      { key: 'sp_y_n', label: 'SP', width: 'w-12' },
      { key: 'csr_comments', label: 'CSR Comments', width: 'w-32' },
      { key: 'routing_notes', label: 'Routing Notes', width: 'w-32' },
      { key: 'loads', label: 'Loads', width: 'w-16' },
      { key: 'carrier', label: 'Carrier', width: 'w-20', sortable: true },
      { key: 'time', label: 'Time', width: 'w-16' },
      { key: 'ship_date', label: 'Ship Date', width: 'w-20', sortable: true },
      { key: 'routed_date', label: 'Route Date', width: 'w-20', sortable: true },
      { key: 'shipped', label: 'Shipped', width: 'w-16', sortable: true },
      { key: 'img_notes', label: 'IMG Notes', width: 'w-32' }
    ];

    // Get unique values for filters
    const uniqueLogDivs = [...new Set((lapLogData || []).map(r => r.log_division).filter(Boolean))].sort();
    const uniqueRabiDivs = [...new Set((lapLogData || []).map(r => r.rabi_division).filter(Boolean))].sort();
    const uniqueShipTo = [...new Set((lapLogData || []).map(r => r.ship_to).filter(Boolean))].sort();
    const uniqueCustomers = [...new Set((lapLogData || []).map(r => r.customer_name).filter(Boolean))].sort();

    const handleSort = (key) => {
      setLapLogSort(prev => ({
        key,
        dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
      }));
    };

    return (
      <div className="space-y-4">
        {/* Header with sync controls */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900">LAP Shipping Log</h2>
              {lapLogLastSync && (
                <span className="text-xs text-gray-500">
                  Last sync: {new Date(lapLogLastSync).toLocaleString()}
                </span>
              )}
              {lapLogData && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {filteredLapLogData.length} / {lapLogData.length} rows
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.open(ROUTING_SHEET_URL, '_blank')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Open Sheet
              </button>
              <button
                onClick={syncLAPLog}
                disabled={lapLogSyncing}
                className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${lapLogSyncing ? 'animate-spin' : ''}`} />
                {lapLogSyncing ? 'Syncing...' : 'Sync from Sheets'}
              </button>
              <button
                onClick={loadLAPLogData}
                disabled={lapLogLoading}
                className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${lapLogLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs font-medium text-gray-500">Filters:</span>
            <input
              type="text"
              value={lapLogFilters.pick_ticket}
              onChange={(e) => setLapLogFilters(prev => ({ ...prev, pick_ticket: e.target.value }))}
              placeholder="Search Pick Ticket..."
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-32"
            />
            <select
              value={lapLogFilters.log_division}
              onChange={(e) => setLapLogFilters(prev => ({ ...prev, log_division: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Log Div</option>
              {uniqueLogDivs.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={lapLogFilters.rabi_division}
              onChange={(e) => setLapLogFilters(prev => ({ ...prev, rabi_division: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Rabi Div</option>
              {uniqueRabiDivs.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select
              value={lapLogFilters.customer_name}
              onChange={(e) => setLapLogFilters(prev => ({ ...prev, customer_name: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Customer</option>
              {uniqueCustomers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={lapLogFilters.ship_to}
              onChange={(e) => setLapLogFilters(prev => ({ ...prev, ship_to: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Ship To</option>
              {uniqueShipTo.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={lapLogFilters.shipped}
              onChange={(e) => setLapLogFilters(prev => ({ ...prev, shipped: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Shipped</option>
              <option value="Y">Shipped</option>
              <option value="N">Not Shipped</option>
            </select>
            {(lapLogFilters.log_division || lapLogFilters.rabi_division || lapLogFilters.ship_to || lapLogFilters.shipped || lapLogFilters.customer_name || lapLogFilters.pick_ticket) && (
              <button
                onClick={() => setLapLogFilters({ log_division: '', rabi_division: '', ship_to: '', shipped: '', customer_name: '', pick_ticket: '' })}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Data table */}
        <div className="bg-white rounded-lg shadow">
          {lapLogLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
              <span className="ml-2 text-gray-600">Loading LAP Log...</span>
            </div>
          ) : !lapLogData || lapLogData.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-2" />
              <p>No LAP Log data available</p>
              <button
                onClick={syncLAPLog}
                className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700"
              >
                Sync from Google Sheets
              </button>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full text-xs border-collapse" style={{ minWidth: '1600px' }}>
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b-2 border-gray-200">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        onClick={() => col.sortable && handleSort(col.key)}
                        className={`px-2 py-2 text-left font-semibold text-gray-600 ${col.width} whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          {col.sortable && lapLogSort.key === col.key && (
                            lapLogSort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredLapLogData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className={`px-2 py-1.5 text-gray-700 ${col.width} whitespace-nowrap overflow-hidden text-ellipsis`}
                          title={row[col.key] || ''}
                        >
                          {col.key === 'shipped' ? (
                            row[col.key] === 'Y' || row[col.key] === 'Yes' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : row[col.key] ? (
                              <span className="text-gray-400">{row[col.key]}</span>
                            ) : '—'
                          ) : (
                            row[col.key] || '—'
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Container Log tab
  const renderContainerLogTab = () => {
    const columns = [
      { key: 'source', label: 'WH', width: 'w-14', sortable: true },
      { key: 'wh_code', label: 'WH Code', width: 'w-20', sortable: true },
      { key: 'container_number', label: 'Container #', width: 'w-28', sortable: true },
      { key: 'status', label: 'Status', width: 'w-20', sortable: true },
      { key: 'trucking_company', label: 'Trucking Co', width: 'w-28', sortable: true },
      { key: 'wh_arrival_date', label: 'Arrival', width: 'w-20', sortable: true },
      { key: 'date_emptied', label: 'Emptied', width: 'w-20', sortable: true },
      { key: 'date_notified_emptied', label: 'Notified', width: 'w-20', sortable: true },
      { key: 'date_944_sent', label: '944 Sent', width: 'w-20', sortable: true },
      { key: 'date_left_yard', label: 'Left Yard', width: 'w-20', sortable: true },
      { key: 'days_on_site', label: 'Days', width: 'w-14', sortable: true },
      { key: 'estimated_cost_of_holding', label: 'Est Cost', width: 'w-20', sortable: true },
      { key: 'amount_of_cases', label: 'Cases', width: 'w-16', sortable: true },
      { key: 'styles', label: 'Styles', width: 'w-24' },
      { key: 'carton_id_changes', label: 'Carton ID Changes', width: 'w-32' },
      { key: 'comments', label: 'Comments', width: 'w-40' }
    ];

    // Get unique values for filters
    const uniqueWhCodes = [...new Set((containerLogData || []).map(r => r.wh_code).filter(Boolean))].sort();
    const uniqueStatuses = [...new Set((containerLogData || []).map(r => r.status).filter(Boolean))].sort();
    const uniqueSources = [...new Set((containerLogData || []).map(r => r.source).filter(Boolean))].sort();

    const handleSort = (key) => {
      setContainerLogSort(prev => ({
        key,
        dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
      }));
    };

    // Handle matrix cell click
    const handleMatrixClick = (status, daysRange) => {
      setContainerLogFilters(prev => ({
        ...prev,
        status: prev.status === status && prev.days_range === daysRange ? '' : status,
        days_range: prev.status === status && prev.days_range === daysRange ? '' : daysRange
      }));
    };

    // Check if a cell is selected
    const isCellSelected = (status, daysRange) => {
      return containerLogFilters.status === status && containerLogFilters.days_range === daysRange;
    };

    return (
      <div className="space-y-4">
        {/* Header with controls */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900">Container Log</h2>
              {containerLogLastSync && (() => {
                const syncTime = new Date(containerLogLastSync);
                const hoursAgo = (Date.now() - syncTime.getTime()) / (1000 * 60 * 60);
                const colorClass = hoursAgo >= 48 ? 'text-red-600 font-medium' : hoursAgo >= 24 ? 'text-yellow-600 font-medium' : 'text-gray-500';
                return (
                  <span className={`text-xs ${colorClass}`}>
                    Last sync: {syncTime.toLocaleString()}
                  </span>
                );
              })()}
              {containerLogData && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {filteredContainerLogData.length} / {containerLogData.length} rows
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadContainerLogData}
                disabled={containerLogLoading}
                className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${containerLogLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Container Matrix */}
        {containerLogData && containerLogData.length > 0 && (
          <div className="bg-white rounded-lg shadow px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Container Status by Days on Site</h3>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600 border-b-2 border-gray-200">Status</th>
                    {containerMatrix.dayRanges.map(range => (
                      <th key={range} className="px-3 py-2 text-center font-semibold text-gray-600 border-b-2 border-gray-200 min-w-[60px]">
                        {range} days
                      </th>
                    ))}
                    <th className="px-3 py-2 text-center font-semibold text-gray-600 border-b-2 border-gray-200 bg-gray-50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {containerMatrix.statuses.map(status => (
                    <tr key={status} className="border-b border-gray-100">
                      <td className="px-3 py-2 font-medium text-gray-700">{status}</td>
                      {containerMatrix.dayRanges.map(range => {
                        const count = containerMatrix.matrix[status]?.[range] || 0;
                        const isSelected = isCellSelected(status, range);
                        return (
                          <td
                            key={range}
                            onClick={() => count > 0 && handleMatrixClick(status, range)}
                            className={`px-3 py-2 text-center transition-colors ${
                              count > 0
                                ? `cursor-pointer hover:bg-blue-100 ${isSelected ? 'bg-blue-200 font-bold' : ''}`
                                : 'text-gray-300'
                            }`}
                          >
                            {count || '—'}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center font-semibold bg-gray-50">
                        {containerMatrix.totals.byStatus[status] || 0}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-2">Total</td>
                    {containerMatrix.dayRanges.map(range => (
                      <td key={range} className="px-3 py-2 text-center">
                        {containerMatrix.totals.byDays[range] || 0}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center bg-gray-100">
                      {containerMatrix.totals.total || 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {(containerLogFilters.status || containerLogFilters.days_range) && (
              <div className="mt-2 text-xs text-blue-600">
                Filtered: {containerLogFilters.status} / {containerLogFilters.days_range} days
                <button
                  onClick={() => setContainerLogFilters(prev => ({ ...prev, status: '', days_range: '' }))}
                  className="ml-2 text-blue-700 hover:text-blue-900 underline"
                >
                  Clear matrix filter
                </button>
              </div>
            )}
          </div>
        )}

        {/* Filters row */}
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium text-gray-500">Filters:</span>
            <input
              type="text"
              value={containerLogFilters.container_number}
              onChange={(e) => setContainerLogFilters(prev => ({ ...prev, container_number: e.target.value }))}
              placeholder="Search Container #..."
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white w-36"
            />
            <select
              value={containerLogFilters.source}
              onChange={(e) => setContainerLogFilters(prev => ({ ...prev, source: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">All WH</option>
              {uniqueSources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={containerLogFilters.wh_code}
              onChange={(e) => setContainerLogFilters(prev => ({ ...prev, wh_code: e.target.value }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">WH Code</option>
              {uniqueWhCodes.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
            <select
              value={containerLogFilters.status}
              onChange={(e) => setContainerLogFilters(prev => ({ ...prev, status: e.target.value, days_range: '' }))}
              className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
            >
              <option value="">Status</option>
              {uniqueStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(containerLogFilters.wh_code || containerLogFilters.status || containerLogFilters.source || containerLogFilters.container_number || containerLogFilters.days_range) && (
              <button
                onClick={() => setContainerLogFilters({ wh_code: '', status: '', source: '', container_number: '', days_range: '' })}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Data table */}
        <div className="bg-white rounded-lg shadow">
          {containerLogLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
              <span className="ml-2 text-gray-600">Loading Container Log...</span>
            </div>
          ) : !containerLogData || containerLogData.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-2" />
              <p>No Container Log data available</p>
              <button
                onClick={loadContainerLogData}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Load Data
              </button>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="w-full text-xs border-collapse" style={{ minWidth: '1600px' }}>
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b-2 border-gray-200">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        onClick={() => col.sortable && handleSort(col.key)}
                        className={`px-2 py-2 text-left font-semibold text-gray-600 ${col.width} whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                      >
                        <span className="flex items-center gap-1">
                          {col.label}
                          {col.sortable && containerLogSort.key === col.key && (
                            containerLogSort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredContainerLogData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-blue-50/50 transition-colors">
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className={`px-2 py-1.5 text-gray-700 ${col.width} whitespace-nowrap overflow-hidden text-ellipsis`}
                          title={row[col.key] || ''}
                        >
                          {col.key === 'source' ? (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${row[col.key] === 'BL' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                              {row[col.key]}
                            </span>
                          ) : (
                            row[col.key] || '—'
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle section change
  const handleSectionChange = (section) => {
    setActiveSection(section);
    // Set default tab for each section
    if (section === 'warehouse') {
      setActiveTab('bl');
    } else {
      setActiveTab('walmart');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Logistics</h1>
          <p className="text-slate-500 mt-1">
            {activeSection === 'warehouse' ? 'Warehouse operations & container tracking' : 'Customer TMS & routing management'}
          </p>
        </div>
        <button
          onClick={() => {
            if (activeSection === 'tms' && activeTab === 'walmart') loadWalmartData();
            // CHA-463 — refresh whichever Target sub-tab is showing, not always routing.
            if (activeSection === 'tms' && activeTab === 'target') {
              if (targetSubTab === 'dims') loadDimsData(); else loadTargetData();
            }
            if (activeSection === 'warehouse' && activeTab === 'bl') loadBLLogData();
            if (activeSection === 'warehouse' && activeTab === 'lap') loadLAPLogData();
          }}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg disabled:opacity-50 transition-colors ${
            activeSection === 'warehouse' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'
          }`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Main Section Navigation */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => handleSectionChange('warehouse')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeSection === 'warehouse'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Building2 className="h-4 w-4" />
              Warehouse
            </div>
          </button>
          <button
            onClick={() => handleSectionChange('tms')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeSection === 'tms'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Truck className="h-4 w-4" />
              Customer TMS
            </div>
          </button>
        </div>

        {/* Sub-tabs for Warehouse */}
        {activeSection === 'warehouse' && (
          <div className="flex border-b border-slate-100 bg-slate-50/50">
            {[
              { id: 'bl', label: 'BL Log', icon: FileText },
              { id: 'lap', label: 'LAP Log', icon: FileText },
              { id: 'capacity', label: 'Capacity', icon: Package },
              { id: 'billing', label: 'Billing', icon: DollarSign }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setError(null); }}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 -mb-px'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Sub-tabs for Customer TMS */}
        {activeSection === 'tms' && (
          <div className="flex border-b border-slate-100 bg-slate-50/50">
            {/* Walmart Tab */}
            <button
              onClick={() => setActiveTab('walmart')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'walmart'
                  ? 'text-[#0071ce] border-b-2 border-[#0071ce] -mb-px'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <svg viewBox="0 0 32 32" className="h-4 w-4">
                <g fill="#ffc220">
                  <path d="M16 3 L17.5 11 L16 14 L14.5 11 Z"/>
                  <path d="M16 29 L17.5 21 L16 18 L14.5 21 Z"/>
                  <path d="M3 16 L11 14.5 L14 16 L11 17.5 Z"/>
                  <path d="M29 16 L21 14.5 L18 16 L21 17.5 Z"/>
                  <path d="M6.5 6.5 L12 10.5 L13.5 13.5 L10.5 12 Z"/>
                  <path d="M25.5 25.5 L20 21.5 L18.5 18.5 L21.5 20 Z"/>
                  <path d="M25.5 6.5 L21.5 12 L18.5 13.5 L20 10.5 Z"/>
                  <path d="M6.5 25.5 L10.5 20 L13.5 18.5 L12 21.5 Z"/>
                </g>
              </svg>
              <span className="font-bold">Walmart</span>
            </button>
            {/* Target Tab */}
            <button
              onClick={() => setActiveTab('target')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'target'
                  ? 'text-[#cc0000] border-b-2 border-[#cc0000] -mb-px'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <svg viewBox="0 0 1615 1551" className="h-4 w-4">
                <circle cx="808" cy="775" r="775" fill="#cc0000"/>
                <circle cx="808" cy="775" r="530" fill="#fff"/>
                <circle cx="808" cy="775" r="285" fill="#cc0000"/>
              </svg>
              <span className="font-medium">Target</span>
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-500 h-5 w-5" />
          <div>
            <p className="text-red-800 font-medium">Error loading data</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-2 text-slate-600">Loading...</span>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <>
          {/* Warehouse Content */}
          {activeSection === 'warehouse' && activeTab === 'bl' && renderBLLogTab()}
          {activeSection === 'warehouse' && activeTab === 'lap' && renderLAPLogTab()}
          {activeSection === 'warehouse' && activeTab === 'capacity' && renderPlaceholder('Warehouse Capacity')}

          {/* Warehouse Billing */}
          {activeSection === 'warehouse' && activeTab === 'billing' && (
            <div className="space-y-4">
              {/* Owner selector */}
              <div className="flex items-center gap-2">
                {['BL'].map(owner => (
                  <button
                    key={owner}
                    onClick={() => setBillingOwner(owner)}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                      billingOwner === owner
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {owner}
                  </button>
                ))}
              </div>

              {/* Billing type tabs */}
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {[
                  { id: 'storage', label: 'Storage', icon: Archive },
                  { id: 'inbound', label: 'Inbound', icon: ArrowDownToLine },
                  { id: 'outbound', label: 'Outbound', icon: ArrowUpFromLine },
                ].map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setBillingTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        billingTab === tab.id
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Storage content */}
              {billingTab === 'storage' && (
                storageLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-6 w-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-slate-600">Loading storage data...</span>
                  </div>
                ) : storageData ? (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-700">Locations by Division</h3>
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => {
                            const next = !excludeUnder30;
                            setExcludeUnder30(next);
                            setStorageData(null);
                            loadStorageData(next ? 30 : null);
                          }}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                            excludeUnder30
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {excludeUnder30 ? '30+ days only' : 'Exclude < 30 days'}
                        </button>
                        <span className="text-xs text-slate-400">
                          Total: {storageData.reduce((sum, r) => sum + (Number(r.location_count) || 0), 0).toLocaleString()} locations
                        </span>
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Division</th>
                          <th className="text-right px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Locations</th>
                          <th className="text-right px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Cases</th>
                          <th className="text-right px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Pieces</th>
                        </tr>
                      </thead>
                      <tbody>
                        {storageData.map((row, i) => (
                          <tr key={row.division} className={`border-b border-slate-50 ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                            <td className="px-5 py-2 font-medium text-slate-700">{row.division}</td>
                            <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(row.location_count).toLocaleString()}</td>
                            <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(row.total_cases).toLocaleString()}</td>
                            <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(row.total_pieces).toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                          <td className="px-5 py-2 text-slate-800">Total</td>
                          <td className="px-5 py-2 text-right font-mono text-slate-800">{storageData.reduce((s, r) => s + (Number(r.location_count) || 0), 0).toLocaleString()}</td>
                          <td className="px-5 py-2 text-right font-mono text-slate-800">{storageData.reduce((s, r) => s + (Number(r.total_cases) || 0), 0).toLocaleString()}</td>
                          <td className="px-5 py-2 text-right font-mono text-slate-800">{storageData.reduce((s, r) => s + (Number(r.total_pieces) || 0), 0).toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null
              )}

              {/* Inbound content */}
              {billingTab === 'inbound' && (
                inboundLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-6 w-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-slate-600">Loading inbound data...</span>
                  </div>
                ) : inboundData ? (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-slate-700">Containers Received — LAP Warehouse</h3>
                        <div className="flex items-center gap-1">
                          <input type="date" value={inboundFrom} onChange={e => setInboundFrom(e.target.value)}
                            className="text-xs border border-slate-300 rounded px-2 py-1" />
                          <span className="text-xs text-slate-400">to</span>
                          <input type="date" value={inboundTo} onChange={e => setInboundTo(e.target.value)}
                            className="text-xs border border-slate-300 rounded px-2 py-1" />
                          <button onClick={() => { setInboundData(null); loadInboundData(inboundFrom, inboundTo); }}
                            className="ml-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                            Go
                          </button>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">
                        {inboundData.length} containers — {inboundData.reduce((s, r) => s + (Number(r.cartons) || 0), 0).toLocaleString()} total cartons
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Container</th>
                          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Received Date</th>
                          <th className="text-right px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Cartons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inboundData.map((row, i) => (
                          <tr key={row.container} className={`border-b border-slate-50 ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                            <td className="px-5 py-2 font-mono font-medium text-slate-700">{row.container}</td>
                            <td className="px-5 py-2 text-slate-600">{row.received_date}</td>
                            <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(row.cartons || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                          <td className="px-5 py-2 text-slate-800">Total</td>
                          <td className="px-5 py-2 text-slate-800">{inboundData.length} containers</td>
                          <td className="px-5 py-2 text-right font-mono text-slate-800">{inboundData.reduce((s, r) => s + (Number(r.cartons) || 0), 0).toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null
              )}

              {/* Outbound content */}
              {billingTab === 'outbound' && (
                outboundLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-6 w-6 text-blue-600 animate-spin" />
                    <span className="ml-2 text-slate-600">Loading outbound data...</span>
                  </div>
                ) : outboundData ? (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-slate-700">Cartons Shipped by Customer — LAP Warehouse</h3>
                        <div className="flex items-center gap-1">
                          <input type="date" value={outboundFrom} onChange={e => setOutboundFrom(e.target.value)}
                            className="text-xs border border-slate-300 rounded px-2 py-1" />
                          <span className="text-xs text-slate-400">to</span>
                          <input type="date" value={outboundTo} onChange={e => setOutboundTo(e.target.value)}
                            className="text-xs border border-slate-300 rounded px-2 py-1" />
                          <button onClick={() => { setOutboundData(null); loadOutboundData(outboundFrom, outboundTo); }}
                            className="ml-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                            Go
                          </button>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">
                        {outboundData.length} customers — {outboundData.reduce((s, r) => s + (Number(r.total_cartons) || 0), 0).toLocaleString()} total cartons
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="text-left px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Customer</th>
                          <th className="text-right px-5 py-2 text-xs font-semibold text-slate-500 uppercase">Cartons</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outboundData.map((row, i) => (
                          <tr key={row.customer} className={`border-b border-slate-50 ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                            <td className="px-5 py-2 font-medium text-slate-700">{row.customer}</td>
                            <td className="px-5 py-2 text-right font-mono text-slate-600">{Number(row.total_cartons || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
                          <td className="px-5 py-2 text-slate-800">Total</td>
                          <td className="px-5 py-2 text-right font-mono text-slate-800">{outboundData.reduce((s, r) => s + (Number(r.total_cartons) || 0), 0).toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null
              )}
            </div>
          )}

          {/* Customer TMS Content */}
          {activeSection === 'tms' && activeTab === 'walmart' && renderWalmartTab()}
          {activeSection === 'tms' && activeTab === 'target' && (
            <div className="space-y-4">
              {/* CHA-463 — Routing and Item Dims sit side by side under Target */}
              <div className="flex gap-1 border-b border-slate-100">
                {/* Both buttons clear `error` (CodeRabbit review): a failure raised by the
                    routing load would otherwise stay in the banner after switching, and the
                    operator would read a routing failure as a dims failure. The warehouse
                    sub-tabs already do this. */}
                <button
                  onClick={() => { setError(null); setTargetSubTab('routing'); }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    targetSubTab === 'routing'
                      ? 'text-[#cc0000] border-b-2 border-[#cc0000] -mb-px'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Routing
                </button>
                <button
                  onClick={() => { setError(null); setTargetSubTab('dims'); }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    targetSubTab === 'dims'
                      ? 'text-[#cc0000] border-b-2 border-[#cc0000] -mb-px'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Item Dims
                </button>
              </div>
              {targetSubTab === 'routing' ? renderTargetTab() : renderTargetDimsTab()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
